// src/pages/TournamentDetails.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Layout from "../components/Layout";
import { auth, db, functions } from "../firebase";
import {
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
  Timestamp,
  collection,
  onSnapshot,
  query,
  orderBy,
  limit,
  deleteField,
} from "firebase/firestore";
import {
  formatTimeFromDate,
  msToMMSS,
  formatMoney,
  computeRoundedPayouts,
} from "./tournamentDetails/helpers";

import EliminationModal from "./tournamentDetails/EliminationModal";
import EntrantsPanel from "./tournamentDetails/EntrantsPanel";

import FormatCard from "./tournamentDetails/components/FormatCard";
import PayoutsCard from "./tournamentDetails/components/PayoutsCard";
import ChipDenominationsCard from "./tournamentDetails/components/ChipDenominationsCard";
import TournamentClockCard from "./tournamentDetails/components/TournamentClockCard";
import TournamentTimelineCard from "./tournamentDetails/components/TournamentTimelineCard";
import BlindStructureCard from "./tournamentDetails/components/BlindStructureCard";
import EarlyBirdCard from "./tournamentDetails/components/EarlyBirdCard";

import { useNavigate, useParams } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
import {
  addEntrantByAdmin,
  removeEntrantWhileRegistering,
} from "./tournamentDetails/actions/tournamentActions";

/* =========================
   Helpers
========================= */
function entrantLabel(e) {
  return e?.displayName || e?.name || e?.email || e?.uid || e?.id || "Player";
}

function toIntOrNull(v) {
  if (v == null) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return Math.floor(n);
}

function toLocalDatetimeInput(value) {
  if (!value) return "";

  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toTimestampFromLocalDatetime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Timestamp.fromDate(d);
}

function formatAdminDateTime(value) {
  if (!value) return "Never";

  const d =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value instanceof Date
      ? value
      : new Date(value);

  if (Number.isNaN(d.getTime())) return "Never";

  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getSeatTableNumber(entrant) {
  const candidates = [
    entrant?.tableNumber,
    entrant?.table,
    entrant?.seatAssignment?.tableNumber,
    entrant?.seatAssignment?.table,
  ];

  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }

  return null;
}

function getSeatNumber(entrant) {
  const candidates = [
    entrant?.seat,
    entrant?.seatNumber,
    entrant?.seatIndex,
    entrant?.seatAssignment?.seat,
    entrant?.seatAssignment?.seatNumber,
    entrant?.seatAssignment?.seatIndex,
  ];

  for (const c of candidates) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }

  return null;
}

function getSeatRole(entrant) {
  return (
    entrant?.seatRole ||
    entrant?.seatLabel ||
    entrant?.position ||
    entrant?.seatAssignment?.seatRole ||
    entrant?.seatAssignment?.seatLabel ||
    entrant?.seatAssignment?.position ||
    ""
  );
}

function hasSeatAssignment(entrant) {
  return getSeatTableNumber(entrant) != null && getSeatNumber(entrant) != null;
}

function sortEntrantsForDisplay(list) {
  const entrants = Array.isArray(list) ? [...list] : [];

  entrants.sort((a, b) => {
    const aElim = !!a?.eliminated;
    const bElim = !!b?.eliminated;

    if (aElim !== bElim) return aElim ? 1 : -1;

    const aHasSeat = hasSeatAssignment(a);
    const bHasSeat = hasSeatAssignment(b);

    if (!aElim && !bElim && aHasSeat && bHasSeat) {
      const aTable = getSeatTableNumber(a) || 999;
      const bTable = getSeatTableNumber(b) || 999;
      if (aTable !== bTable) return aTable - bTable;

      const aSeat = getSeatNumber(a) || 999;
      const bSeat = getSeatNumber(b) || 999;
      if (aSeat !== bSeat) return aSeat - bSeat;
    }

    if (!aElim && !bElim && aHasSeat !== bHasSeat) {
      return aHasSeat ? -1 : 1;
    }

    if (aElim && bElim) {
      return Number(a?.finishPlace || 9999) - Number(b?.finishPlace || 9999);
    }

    const aCreated =
      typeof a?.createdAt?.toMillis === "function"
        ? a.createdAt.toMillis()
        : new Date(a?.createdAt || 0).getTime() || 0;

    const bCreated =
      typeof b?.createdAt?.toMillis === "function"
        ? b.createdAt.toMillis()
        : new Date(b?.createdAt || 0).getTime() || 0;

    return aCreated - bCreated;
  });

  return entrants;
}

function groupActiveEntrantsByTable(entrants) {
  const active = (Array.isArray(entrants) ? entrants : []).filter(
    (e) => !e?.eliminated && hasSeatAssignment(e)
  );

  const map = new Map();

  active.forEach((entrant) => {
    const tableNumber = getSeatTableNumber(entrant);
    if (tableNumber == null) return;

    if (!map.has(tableNumber)) {
      map.set(tableNumber, []);
    }
    map.get(tableNumber).push(entrant);
  });

  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([tableNumber, players]) => ({
      tableNumber,
      players: [...players].sort((a, b) => {
        return (getSeatNumber(a) || 999) - (getSeatNumber(b) || 999);
      }),
    }));
}

function StatusPill({ children, tone = "zinc" }) {
  const styles =
    tone === "red"
      ? "border-red-500/30 bg-red-500/10 text-red-300"
      : tone === "green"
      ? "border-green-500/30 bg-green-500/10 text-green-300"
      : tone === "blue"
      ? "border-blue-500/30 bg-blue-500/10 text-blue-300"
      : tone === "amber"
      ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
      : "border-zinc-700 bg-zinc-800/70 text-zinc-200";

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${styles}`}
    >
      {children}
    </span>
  );
}

function InfoCard({ label, value, subtext }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
      <p className="text-zinc-500 text-sm">{label}</p>
      <p className="text-zinc-100 font-semibold mt-1">{value}</p>
      {subtext ? <p className="text-zinc-500 text-xs mt-2">{subtext}</p> : null}
    </div>
  );
}

function TableCombineBanner({
  combine,
  maxPlayersPerTable,
  activeTableCount,
  requiredTables,
  playersRemaining,
  onGenerate,
  onResume,
  generating,
  canResume,
}) {
  if (!combine?.active) return null;

  return (
    <div className="mb-6 rounded-xl border border-amber-500/30 bg-amber-500/10 p-5">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <div className="flex flex-wrap gap-2 mb-3">
            <StatusPill tone="amber">Table Combine Required</StatusPill>
            <StatusPill tone="blue">{playersRemaining} Players Remaining</StatusPill>
            <StatusPill tone="zinc">
              {activeTableCount} Active Tables → {requiredTables} Needed
            </StatusPill>
          </div>

          <h3 className="text-lg font-semibold text-amber-200">
            Pause and reseat before continuing
          </h3>

          <p className="text-sm text-amber-100/90 mt-2 leading-relaxed">
            The field now fits on fewer tables. Generate fresh seating, move players,
            and then resume the clock when everyone is seated.
          </p>

          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            <InfoCard label="Players Remaining" value={String(playersRemaining)} />
            <InfoCard label="Max Per Table" value={String(maxPlayersPerTable)} />
            <InfoCard label="Target Tables" value={String(requiredTables)} />
          </div>

          {combine?.triggeredAt ? (
            <p className="text-xs text-amber-200/80 mt-4">
              Triggered: {formatAdminDateTime(combine.triggeredAt)}
            </p>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 min-w-[240px]">
          <button
            type="button"
            onClick={onGenerate}
            disabled={generating}
            className={`px-4 py-3 rounded font-semibold ${
              generating
                ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
            }`}
          >
            {generating ? "Generating..." : "Generate / Redraw Seats"}
          </button>

          <button
            type="button"
            onClick={onResume}
            disabled={!canResume}
            className={`px-4 py-3 rounded font-semibold ${
              !canResume
                ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                : "bg-green-700 hover:bg-green-800 text-white"
            }`}
          >
            Confirm Seating & Resume
          </button>
        </div>
      </div>
    </div>
  );
}

function WinnerConfirmModal({
  open,
  winner,
  runnerUp,
  payoutAmountForPlace,
  onCancel,
  onConfirm,
  submitting,
}) {
  if (!open || !winner || !runnerUp) return null;

  return (
    <div className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm px-4 py-6 flex items-center justify-center">
      <div className="w-full max-w-lg rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl">
        <div className="px-5 py-4 border-b border-zinc-800">
          <h3 className="text-xl font-semibold text-[#B87333]">
            Finalize Heads-Up Results
          </h3>
          <p className="text-zinc-400 text-sm mt-1">
            Confirm first and second place before completing the tournament.
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div className="rounded-lg border border-green-500/20 bg-green-500/10 p-4">
            <div className="text-sm text-green-300 font-semibold">1st Place</div>
            <div className="text-zinc-100 text-lg font-semibold mt-1">
              {entrantLabel(winner)}
            </div>
            <div className="text-zinc-400 text-sm mt-1">
              Payout: {formatMoney(payoutAmountForPlace(1))}
            </div>
          </div>

          <div className="rounded-lg border border-blue-500/20 bg-blue-500/10 p-4">
            <div className="text-sm text-blue-300 font-semibold">2nd Place</div>
            <div className="text-zinc-100 text-lg font-semibold mt-1">
              {entrantLabel(runnerUp)}
            </div>
            <div className="text-zinc-400 text-sm mt-1">
              Payout: {formatMoney(payoutAmountForPlace(2))}
            </div>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-zinc-800 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="px-4 py-3 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting}
            className="px-4 py-3 rounded bg-green-700 hover:bg-green-800 text-white font-semibold disabled:opacity-60"
          >
            {submitting ? "Saving..." : "Confirm Winner & Complete Tournament"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TournamentDetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);

  const [blinds, setBlinds] = useState([]);
  const [savingStructure, setSavingStructure] = useState(false);

  const [entrants, setEntrants] = useState([]);
  const [loadingEntrants, setLoadingEntrants] = useState(true);
  const [timelineEvents, setTimelineEvents] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(true);

  const [chips, setChips] = useState([]);
  const [chipValue, setChipValue] = useState("");
  const [chipColor, setChipColor] = useState("#B87333");
  const [savingChips, setSavingChips] = useState(false);

  const [payouts, setPayouts] = useState([]);
  const [payoutPlaces, setPayoutPlaces] = useState(3);
  const [savingPayouts, setSavingPayouts] = useState(false);

  const [savingBasicInfo, setSavingBasicInfo] = useState(false);
  const [basicInfoDirty, setBasicInfoDirty] = useState(false);

  const [nameDraft, setNameDraft] = useState("");
  const [locationDraft, setLocationDraft] = useState("");
  const [dateTimeDraft, setDateTimeDraft] = useState("");
  const [buyInDraft, setBuyInDraft] = useState("0");
  const [startingStackDraft, setStartingStackDraft] = useState("1500");

  const [savingEmailSettings, setSavingEmailSettings] = useState(false);
  const [emailDirty, setEmailDirty] = useState(false);
  const [sendAnnouncementEmailNowDraft, setSendAnnouncementEmailNowDraft] = useState(false);
  const [sendEarlyBirdReminderEmailDraft, setSendEarlyBirdReminderEmailDraft] = useState(false);
  const [sendTournamentReminderEmailDraft, setSendTournamentReminderEmailDraft] = useState(false);

  const [savingSmsSettings, setSavingSmsSettings] = useState(false);
  const [smsDirty, setSmsDirty] = useState(false);
  const [sendingManualReminderText, setSendingManualReminderText] = useState(false);
  const [sendAnnouncementTextNowDraft, setSendAnnouncementTextNowDraft] = useState(false);
  const [sendEarlyBirdReminderTextDraft, setSendEarlyBirdReminderTextDraft] = useState(false);
  const [sendTournamentReminderTextDraft, setSendTournamentReminderTextDraft] = useState(false);
  const [allowSmsRegistrationDraft, setAllowSmsRegistrationDraft] = useState(true);

  const [savingFormatSettings, setSavingFormatSettings] = useState(false);
  const [formatDirty, setFormatDirty] = useState(false);

  const [rebuysEnabledDraft, setRebuysEnabledDraft] = useState(false);
  const [rebuyCutoffLevelDraft, setRebuyCutoffLevelDraft] = useState("");

  // Tournament type / bounty
  const [tournamentTypeDraft, setTournamentTypeDraft] = useState("standard");
  const [bountyAmountDraft, setBountyAmountDraft] = useState("0");
  const [prizePoolContributionDraft, setPrizePoolContributionDraft] = useState("0");
  const [rebuyPriceDraft, setRebuyPriceDraft] = useState("0");
  const [rebuyPrizePoolContributionDraft, setRebuyPrizePoolContributionDraft] = useState("0");
  const [rebuyBountyAmountDraft, setRebuyBountyAmountDraft] = useState("0");

  // Late registration
  const [lateRegistrationEnabledDraft, setLateRegistrationEnabledDraft] =
    useState(false);
  const [lateRegistrationCutoffLevelDraft, setLateRegistrationCutoffLevelDraft] =
    useState("");

  const [addOnsEnabledDraft, setAddOnsEnabledDraft] = useState(false);
  const [addOnLevelDraft, setAddOnLevelDraft] = useState("");
  const [addOnChipsDraft, setAddOnChipsDraft] = useState("0");
  const [addOnCostDraft, setAddOnCostDraft] = useState("0");

  const [now, setNow] = useState(Date.now());
  const [clockControlSaving, setClockControlSaving] = useState(false);
  const advanceLockRef = useRef(false);
  const combineTriggerLockRef = useRef(false);
  const clockCheckpointLockRef = useRef(false);

  const [elimModalOpen, setElimModalOpen] = useState(false);
  const [elimTarget, setElimTarget] = useState(null);
  const [elimById, setElimById] = useState("");
  const [elimSubmitting, setElimSubmitting] = useState(false);

  const [assigningSeatsState, setAssigningSeatsState] = useState(false);
  const [generatingCombineSeats, setGeneratingCombineSeats] = useState(false);

  const [winnerConfirmOpen, setWinnerConfirmOpen] = useState(false);
  const [winnerConfirmTarget, setWinnerConfirmTarget] = useState(null);
  const [winnerConfirmSubmitting, setWinnerConfirmSubmitting] = useState(false);

  const [savingEarlyBirdSettings, setSavingEarlyBirdSettings] = useState(false);
  const [earlyBirdDirty, setEarlyBirdDirty] = useState(false);

  const [earlyBirdEnabledDraft, setEarlyBirdEnabledDraft] = useState(false);
  const [earlyBirdBonusChipsDraft, setEarlyBirdBonusChipsDraft] = useState("0");
  const [earlyBirdCutoffAtDraft, setEarlyBirdCutoffAtDraft] = useState("");

  const [savingLiveSettings, setSavingLiveSettings] = useState(false);
  const [liveSettingsDirty, setLiveSettingsDirty] = useState(false);
  const [communicationsLiveDraft, setCommunicationsLiveDraft] = useState(false);

  const [savingDealerAppreciationSettings, setSavingDealerAppreciationSettings] =
    useState(false);
  const [dealerAppreciationDirty, setDealerAppreciationDirty] = useState(false);
  const [dealerAppreciationEnabledDraft, setDealerAppreciationEnabledDraft] =
    useState(false);
  const [dealerAppreciationCostDraft, setDealerAppreciationCostDraft] = useState("0");
  const [dealerAppreciationBonusChipsDraft, setDealerAppreciationBonusChipsDraft] =
    useState("0");
  const [dealerAppreciationPrizePoolAmountDraft, setDealerAppreciationPrizePoolAmountDraft] =
    useState("0");
  const [dealerAppreciationDealerAmountDraft, setDealerAppreciationDealerAmountDraft] =
    useState("0");

  /* =========================
     Derived flags
  ========================= */
  const isCompleted = tournament?.status === "completed";
  const isRegistering = tournament?.status === "registering";
  const isRunning = tournament?.status === "running";
  const isCancelled = tournament?.status === "cancelled";

  const communicationsLive = tournament?.communicationsLive === true;
  const showDisplayButton = isAdmin;

  const rebuysEnabled = !!tournament?.rebuysEnabled;
  const rebuyCutoffLevel = toIntOrNull(tournament?.rebuyCutoffLevel);

  const tournamentType =
    tournament?.bountyEnabled === true ||
    String(tournament?.tournamentType || "").toLowerCase() === "bounty"
      ? "bounty"
      : "standard";
  const bountyEnabled = tournamentType === "bounty";
  const bountyAmount = bountyEnabled
    ? Math.max(0, Number(tournament?.bountyAmount || 0))
    : 0;
  const prizePoolContribution = bountyEnabled
    ? Math.max(
        0,
        Number(
          tournament?.prizePoolContribution ??
            Math.max(0, Number(tournament?.buyIn || 0) - bountyAmount)
        )
      )
    : Math.max(0, Number(tournament?.buyIn || 0));

  // Absent values preserve the behavior of existing tournaments.
  const rebuyPrice = Math.max(0, Number(tournament?.rebuyPrice ?? tournament?.buyIn ?? 0));
  const rebuyBountyAmount = bountyEnabled
    ? Math.max(0, Number(tournament?.rebuyBountyAmount ?? bountyAmount))
    : 0;
  const rebuyPrizePoolContribution = bountyEnabled
    ? Math.max(0, Number(tournament?.rebuyPrizePoolContribution ?? prizePoolContribution))
    : rebuyPrice;

  const lateRegistrationEnabled = !!tournament?.lateRegistrationEnabled;
  const lateRegistrationCutoffLevel = toIntOrNull(
    tournament?.lateRegistrationCutoffLevel
  );
  const lateRegistrationOpen =
    isRunning &&
    lateRegistrationEnabled &&
    tournament?.lateRegistrationOpen === true;

  const addOnsEnabled = !!tournament?.addOnsEnabled;
  const addOnLevel = toIntOrNull(tournament?.addOnLevel);
  const addOnChips = Math.max(0, Number(tournament?.addOnChips || 0));
  const addOnCost = Math.max(0, Number(tournament?.addOnCost || 0));

  const earlyBirdEnabled = !!tournament?.earlyBirdEnabled;
  const earlyBirdBonusChips = Math.max(0, Number(tournament?.earlyBirdBonusChips || 0));
  const earlyBirdDeadline =
    tournament?.earlyBirdDeadline || tournament?.earlyBirdCutoffAt || null;

  const dealerAppreciationEnabled = !!tournament?.dealerAppreciationEnabled;
  const dealerAppreciationCost = Math.max(
    0,
    Number(tournament?.dealerAppreciationCost || 0)
  );
  const dealerAppreciationBonusChips = Math.max(
    0,
    Number(tournament?.dealerAppreciationBonusChips || 0)
  );
  const dealerAppreciationPrizePoolAmount = Math.max(
    0,
    Number(tournament?.dealerAppreciationPrizePoolAmount || 0)
  );
  const dealerAppreciationDealerAmount = Math.max(
    0,
    Number(tournament?.dealerAppreciationDealerAmount || 0)
  );

  const sendAnnouncementEmailNow = !!tournament?.sendAnnouncementEmailNow;
  const sendEarlyBirdReminderEmail = !!tournament?.sendEarlyBirdReminderEmail;
  const sendTournamentReminderEmail = !!tournament?.sendTournamentReminderEmail;

  const sendAnnouncementTextNow = !!tournament?.sendAnnouncementTextNow;
  const sendEarlyBirdReminderText = !!tournament?.sendEarlyBirdReminderText;
  const sendTournamentReminderText = !!tournament?.sendTournamentReminderText;

  const smsManualReminderSentAt = tournament?.smsManualReminderSentAt || null;
  const smsManualReminderSentCount = Math.max(
    0,
    Number(tournament?.smsManualReminderSentCount || 0)
  );
  const smsManualReminderSkippedCount = Math.max(
    0,
    Number(tournament?.smsManualReminderSkippedCount || 0)
  );
  const smsManualReminderLastSentByName = String(
    tournament?.smsManualReminderLastSentByName || ""
  ).trim();

  const smsManualReminderSentRecently = useMemo(() => {
    if (!smsManualReminderSentAt) return false;

    const sentAt =
      typeof smsManualReminderSentAt?.toDate === "function"
        ? smsManualReminderSentAt.toDate()
        : smsManualReminderSentAt instanceof Date
        ? smsManualReminderSentAt
        : new Date(smsManualReminderSentAt);

    if (Number.isNaN(sentAt.getTime())) return false;
    return Date.now() - sentAt.getTime() < 24 * 60 * 60 * 1000;
  }, [smsManualReminderSentAt]);

  const addOnPause = useMemo(() => {
    const p = tournament?.addOnPause;
    return p && typeof p === "object" ? p : { active: false };
  }, [tournament?.addOnPause]);

  const addOnPauseActive = !!addOnPause?.active;

  const tableCombine = useMemo(() => {
    const c = tournament?.tableCombine;
    return c && typeof c === "object" ? c : { active: false };
  }, [tournament?.tableCombine]);

  const tableCombineActive = !!tableCombine?.active;

  const dateLabel = useMemo(() => {
    if (!tournament?.date) return "";
    if (tournament.date?.toDate) {
      const d = tournament.date.toDate();
      return `${d.toDateString()} — ${formatTimeFromDate(d)}`;
    }
    return String(tournament.date || "");
  }, [tournament]);

  const clock = useMemo(() => {
    const c = tournament?.clock;
    if (c && typeof c === "object") return c;
    return {
      status: "stopped",
      index: 0,
      endsAt: null,
      startedAt: null,
      pausedAt: null,
      remainingMs: null,
    };
  }, [tournament?.clock]);

  const activeItem = blinds?.[clock.index] || null;

  const levelItems = useMemo(() => {
    return (Array.isArray(blinds) ? blinds : []).filter((b) => b?.type === "level");
  }, [blinds]);

  const levelIndexByLevelNumber = useMemo(() => {
    const m = new Map();
    (Array.isArray(blinds) ? blinds : []).forEach((it, idx) => {
      if (it?.type === "level" && Number(it.level)) m.set(Number(it.level), idx);
    });
    return m;
  }, [blinds]);

  const rebuyCutoffIndex = useMemo(() => {
    if (!rebuyCutoffLevel) return null;
    const idx = levelIndexByLevelNumber.get(Number(rebuyCutoffLevel));
    return typeof idx === "number" ? idx : null;
  }, [rebuyCutoffLevel, levelIndexByLevelNumber]);

  const addOnLevelIndex = useMemo(() => {
    if (!addOnLevel) return null;
    const idx = levelIndexByLevelNumber.get(Number(addOnLevel));
    return typeof idx === "number" ? idx : null;
  }, [addOnLevel, levelIndexByLevelNumber]);

  const rebuysWindowOpen = useMemo(() => {
    if (!isRunning) return false;
    if (!rebuysEnabled) return false;
    if (rebuyCutoffIndex == null) return false;
    return Number(clock?.index || 0) <= rebuyCutoffIndex;
  }, [isRunning, rebuysEnabled, rebuyCutoffIndex, clock?.index]);

  const addOnsWindowOpen = useMemo(() => {
    if (!isRunning) return false;
    if (!addOnsEnabled) return false;
    if (addOnLevelIndex == null) return false;
    return addOnPauseActive === true;
  }, [isRunning, addOnsEnabled, addOnLevelIndex, addOnPauseActive]);

  const entrantsSorted = useMemo(() => {
    return sortEntrantsForDisplay(entrants);
  }, [entrants]);

  const activeEntrants = useMemo(
    () => entrantsSorted.filter((e) => !e.eliminated),
    [entrantsSorted]
  );

  const entrantsCount = Array.isArray(entrants) ? entrants.length : 0;

  const computedRemaining = useMemo(() => {
    // While registration is open, the entrant documents are the source of
    // truth. Cached tournament counters may lag behind SMS/admin registration.
    if (isRegistering) {
      return activeEntrants.length;
    }

    const stored = tournament?.playersRemaining;

    if (
      stored !== null &&
      stored !== undefined &&
      Number.isFinite(Number(stored))
    ) {
      return Math.max(0, Number(stored));
    }

    return activeEntrants.length;
  }, [
    isRegistering,
    tournament?.playersRemaining,
    activeEntrants.length,
  ]);

  const showWinnerControls =
    isAdmin && !isCompleted && computedRemaining === 2;

  const buyIn = useMemo(() => Number(tournament?.buyIn || 0), [tournament]);

  const totalRebuys = useMemo(() => {
    return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
      return sum + Math.max(0, Number(e?.rebuys || 0));
    }, 0);
  }, [entrants]);

  const totalAddOns = useMemo(() => {
    return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
      return sum + (e?.addOnTaken ? 1 : 0);
    }, 0);
  }, [entrants]);

  const totalDealerAppreciationOptIns = useMemo(() => {
    return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
      return sum + (e?.dealerAppreciationOptIn ? 1 : 0);
    }, 0);
  }, [entrants]);

  const totalDealerAppreciationPrizePool = useMemo(() => {
    return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
      return sum + Math.max(0, Number(e?.dealerAppreciationPrizePoolAmount || 0));
    }, 0);
  }, [entrants]);

  const totalDealerAppreciationDealerPayout = useMemo(() => {
    return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
      return sum + Math.max(0, Number(e?.dealerAppreciationDealerAmount || 0));
    }, 0);
  }, [entrants]);

  const totalDealerAppreciationBonusChipsAwarded = useMemo(() => {
    return (Array.isArray(entrants) ? entrants : []).reduce((sum, e) => {
      return sum + Math.max(0, Number(e?.dealerAppreciationBonusChips || 0));
    }, 0);
  }, [entrants]);

  const prizePool = useMemo(() => {
    const explicit = Number(tournament?.finalPrizePool || tournament?.prizePool || 0);
    if (explicit > 0) return explicit;

    // In a bounty tournament, only the configured prize-pool portion of each
    // entry/rebuy belongs in the regular payout pool. Bounty money stays separate.
    const entryPrizeContribution = bountyEnabled ? prizePoolContribution : buyIn;

    const base = Math.max(entrantsCount * entryPrizeContribution, 0);
    const rebuyAdd = Math.max(totalRebuys * rebuyPrizePoolContribution, 0);
    const addOnAdd = Math.max(totalAddOns * addOnCost, 0);
    const dealerAppAdd = Math.max(totalDealerAppreciationPrizePool, 0);

    return base + rebuyAdd + addOnAdd + dealerAppAdd;
  }, [
    tournament,
    entrantsCount,
    buyIn,
    bountyEnabled,
    prizePoolContribution,
    rebuyPrizePoolContribution,
    totalRebuys,
    totalAddOns,
    addOnCost,
    totalDealerAppreciationPrizePool,
  ]);

  const completedCustomChopPayouts = useMemo(() => {
    const isCustomChop =
      isCompleted &&
      String(tournament?.results?.completionType || "").toLowerCase() === "custom_chop";

    if (!isCustomChop) return [];

    const resultPlacements = Array.isArray(tournament?.results?.placements)
      ? tournament.results.placements
      : [];

    const fromResults = resultPlacements
      .map((row) => ({
        place: Number(row?.finishPlace || row?.place || 0),
        amount: Math.max(
          0,
          Number(row?.negotiatedPayout ?? row?.amount ?? 0)
        ),
      }))
      .filter((row) => row.place > 0);

    if (fromResults.length > 0) {
      return fromResults
        .map((row) => ({
          ...row,
          percent:
            prizePool > 0 ? (row.amount / prizePool) * 100 : 0,
        }))
        .sort((a, b) => a.place - b.place);
    }

    const fromEntrants = (Array.isArray(entrants) ? entrants : [])
      .filter(
        (entrant) =>
          entrant?.chopParticipant === true &&
          Number(entrant?.finishPlace || 0) > 0
      )
      .map((entrant) => ({
        place: Number(entrant.finishPlace),
        amount: Math.max(0, Number(entrant?.negotiatedPayout || 0)),
      }));

    return fromEntrants
      .map((row) => ({
        ...row,
        percent:
          prizePool > 0 ? (row.amount / prizePool) * 100 : 0,
      }))
      .sort((a, b) => a.place - b.place);
  }, [isCompleted, tournament, entrants, prizePool]);

  // Preserve the tournament's ORIGINAL saved payout structure as the scheduled
  // payout plan, even if the tournament later ends in a negotiated custom chop.
  const scheduledPayoutRows = useMemo(() => {
    const rawInc = Number(tournament?.payoutRoundingIncrement || 5);
    const incrementValue = rawInc === 10 ? 10 : 5;

    return computeRoundedPayouts(prizePool, payouts, {
      increment: incrementValue,
      rounding: "nearest",
      adjustPlace: "last",
    });
  }, [prizePool, payouts, tournament?.payoutRoundingIncrement]);

  // Actual result payouts are separate from the scheduled payout plan.
  // A completed custom chop uses the exact negotiated amounts for entrant/results
  // displays, while a normally completed tournament uses the scheduled ladder.
  const payoutRows = useMemo(() => {
    if (completedCustomChopPayouts.length > 0) {
      return completedCustomChopPayouts;
    }
    return scheduledPayoutRows;
  }, [completedCustomChopPayouts, scheduledPayoutRows]);

  const payoutByPlace = useMemo(() => {
    const map = new Map();
    payoutRows.forEach((r) => {
      map.set(Number(r.place), {
        amount: Number(r.amount || 0),
        percent: Number(r.percent || 0),
      });
    });
    return map;
  }, [payoutRows]);

  const scheduledPayoutByPlace = useMemo(() => {
    const map = new Map();
    scheduledPayoutRows.forEach((r) => {
      map.set(Number(r.place), {
        amount: Number(r.amount || 0),
        percent: Number(r.percent || 0),
      });
    });
    return map;
  }, [scheduledPayoutRows]);

  const payoutAmountForPlace = (place) => {
    const row = payoutByPlace.get(Number(place));
    return row ? Number(row.amount || 0) : 0;
  };

  const scheduledPayoutAmountForPlace = (place) => {
    const row = scheduledPayoutByPlace.get(Number(place));
    return row ? Number(row.amount || 0) : 0;
  };

  const entrantUidSet = useMemo(() => {
    const s = new Set();
    (Array.isArray(entrants) ? entrants : []).forEach((e) => s.add(String(e.uid || e.id)));
    return s;
  }, [entrants]);

  const maxPlayersPerTable = useMemo(() => {
    const raw = Number(tournament?.maxPlayersPerTable || 9);
    if (!Number.isFinite(raw) || raw < 2) return 9;
    return Math.floor(raw);
  }, [tournament?.maxPlayersPerTable]);

  const seatedTables = useMemo(() => {
    return groupActiveEntrantsByTable(activeEntrants);
  }, [activeEntrants]);

  const activeTableCount = useMemo(() => {
    // "Active" means a table that actually has seated players.
    return seatedTables.length;
  }, [seatedTables]);

  const requiredTables = useMemo(() => {
    if (computedRemaining <= 0) return 0;
    return Math.ceil(computedRemaining / maxPlayersPerTable);
  }, [computedRemaining, maxPlayersPerTable]);

  const combineNeeded = useMemo(() => {
    return (
      isRunning &&
      !isCompleted &&
      !isCancelled &&
      computedRemaining > 1 &&
      activeTableCount > requiredTables
    );
  }, [isRunning, isCompleted, isCancelled, computedRemaining, activeTableCount, requiredTables]);

  const runTournamentControl = useCallback(
    async (action, payload = {}) => {
      if (!isAdmin) {
        throw new Error("Admin access required.");
      }

      const control = httpsCallable(functions, "adminTournamentControl");
      const result = await control({
        tournamentId: id,
        action,
        ...payload,
      });

      return result?.data || {};
    },
    [id, isAdmin]
  );

  const registrationPreflightRef = useRef("");

  useEffect(() => {
    if (!isAdmin || !isRegistering || loadingEntrants || !tournament?.id) {
      return;
    }

    const syncKey = `${tournament.id}:${entrantsCount}`;
    if (registrationPreflightRef.current === syncKey) return;
    registrationPreflightRef.current = syncKey;

    runTournamentControl("preflight").catch((err) => {
      console.error("Registration count preflight failed:", err);
      registrationPreflightRef.current = "";
    });
  }, [
    isAdmin,
    isRegistering,
    loadingEntrants,
    tournament?.id,
    entrantsCount,
    runTournamentControl,
  ]);

  const segmentLabel = useCallback(
    (index) => {
      const item = blinds?.[Number(index || 0)];
      if (!item) return "Unknown segment";
      if (item.type === "break") return item.label || "Break";
      return `Level ${item.level}: ${item.sb}/${item.bb}${item.ante ? ` (${item.ante} ante)` : ""}`;
    },
    [blinds]
  );

  /* =========================
     Sync drafts on load/change

     IMPORTANT:
     Live tournament snapshots can change every few seconds while the clock is
     running. Do not overwrite an admin's unsaved form edits when that happens.
  ========================= */
  useEffect(() => {
    if (!tournament) return;

    if (!basicInfoDirty) {
      setNameDraft(String(tournament?.name || ""));
      setLocationDraft(String(tournament?.location || ""));
      setDateTimeDraft(toLocalDatetimeInput(tournament?.date));
      setBuyInDraft(String(Math.max(0, Number(tournament?.buyIn || 0))));
      setStartingStackDraft(
        String(Math.max(0, Number(tournament?.startingStack || 1500)))
      );
    }

    if (!liveSettingsDirty) {
      setCommunicationsLiveDraft(!!tournament?.communicationsLive);
    }

    if (!emailDirty) {
      setSendAnnouncementEmailNowDraft(!!tournament?.sendAnnouncementEmailNow);
      setSendEarlyBirdReminderEmailDraft(
        !!tournament?.sendEarlyBirdReminderEmail
      );
      setSendTournamentReminderEmailDraft(
        !!tournament?.sendTournamentReminderEmail
      );
    }

    if (!smsDirty) {
      setSendAnnouncementTextNowDraft(!!tournament?.sendAnnouncementTextNow);
      setSendEarlyBirdReminderTextDraft(
        !!tournament?.sendEarlyBirdReminderText
      );
      setSendTournamentReminderTextDraft(
        !!tournament?.sendTournamentReminderText
      );
      setAllowSmsRegistrationDraft(
        tournament?.allowSmsRegistration !== false
      );
    }

    if (!formatDirty) {
      setRebuysEnabledDraft(!!tournament?.rebuysEnabled);
      setRebuyCutoffLevelDraft(
        toIntOrNull(tournament?.rebuyCutoffLevel)
          ? String(Number(tournament.rebuyCutoffLevel))
          : ""
      );

      const nextTournamentType =
        tournament?.bountyEnabled === true ||
        String(tournament?.tournamentType || "").toLowerCase() === "bounty"
          ? "bounty"
          : "standard";

      const nextBountyAmount =
        nextTournamentType === "bounty"
          ? Math.max(0, Number(tournament?.bountyAmount || 0))
          : 0;

      const nextPrizePoolContribution =
        nextTournamentType === "bounty"
          ? Math.max(
              0,
              Number(
                tournament?.prizePoolContribution ??
                  Math.max(
                    0,
                    Number(tournament?.buyIn || 0) - nextBountyAmount
                  )
              )
            )
          : Math.max(0, Number(tournament?.buyIn || 0));

      setTournamentTypeDraft(nextTournamentType);
      setBountyAmountDraft(String(nextBountyAmount));
      setPrizePoolContributionDraft(String(nextPrizePoolContribution));
      setRebuyPriceDraft(
        String(Math.max(0, Number(tournament?.rebuyPrice ?? tournament?.buyIn ?? 0)))
      );
      setRebuyPrizePoolContributionDraft(
        String(
          nextTournamentType === "bounty"
            ? Math.max(0, Number(tournament?.rebuyPrizePoolContribution ?? nextPrizePoolContribution))
            : Math.max(0, Number(tournament?.rebuyPrice ?? tournament?.buyIn ?? 0))
        )
      );
      setRebuyBountyAmountDraft(
        String(
          nextTournamentType === "bounty"
            ? Math.max(0, Number(tournament?.rebuyBountyAmount ?? nextBountyAmount))
            : 0
        )
      );

      setLateRegistrationEnabledDraft(
        !!tournament?.lateRegistrationEnabled
      );
      setLateRegistrationCutoffLevelDraft(
        toIntOrNull(tournament?.lateRegistrationCutoffLevel)
          ? String(Number(tournament.lateRegistrationCutoffLevel))
          : ""
      );

      setAddOnsEnabledDraft(!!tournament?.addOnsEnabled);
      setAddOnLevelDraft(
        toIntOrNull(tournament?.addOnLevel)
          ? String(Number(tournament.addOnLevel))
          : ""
      );
      setAddOnChipsDraft(
        String(Math.max(0, Number(tournament?.addOnChips || 0)))
      );
      setAddOnCostDraft(
        String(Math.max(0, Number(tournament?.addOnCost || 0)))
      );
    }

    if (!earlyBirdDirty) {
      setEarlyBirdEnabledDraft(!!tournament?.earlyBirdEnabled);
      setEarlyBirdBonusChipsDraft(
        String(Math.max(0, Number(tournament?.earlyBirdBonusChips || 0)))
      );
      setEarlyBirdCutoffAtDraft(
        toLocalDatetimeInput(
          tournament?.earlyBirdDeadline || tournament?.earlyBirdCutoffAt
        )
      );
    }

    if (!dealerAppreciationDirty) {
      setDealerAppreciationEnabledDraft(
        !!tournament?.dealerAppreciationEnabled
      );
      setDealerAppreciationCostDraft(
        String(
          Math.max(0, Number(tournament?.dealerAppreciationCost || 0))
        )
      );
      setDealerAppreciationBonusChipsDraft(
        String(
          Math.max(
            0,
            Number(tournament?.dealerAppreciationBonusChips || 0)
          )
        )
      );
      setDealerAppreciationPrizePoolAmountDraft(
        String(
          Math.max(
            0,
            Number(tournament?.dealerAppreciationPrizePoolAmount || 0)
          )
        )
      );
      setDealerAppreciationDealerAmountDraft(
        String(
          Math.max(
            0,
            Number(tournament?.dealerAppreciationDealerAmount || 0)
          )
        )
      );
    }
  }, [
    tournament,
    basicInfoDirty,
    liveSettingsDirty,
    emailDirty,
    smsDirty,
    formatDirty,
    earlyBirdDirty,
    dealerAppreciationDirty,
  ]);

  /* =========================
     Clock tick
  ========================= */
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeRemainingMs = useMemo(() => {
    if (clock.status === "paused") return Number(clock.remainingMs || 0);
    if (!clock?.endsAt?.toMillis) return null;
    return clock.endsAt.toMillis() - now;
  }, [clock, now]);

  /* Persist a lightweight recovery checkpoint while the clock runs. */
  useEffect(() => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (clock.status !== "running") return;
    if (!clock?.endsAt?.toMillis) return;

    const checkpoint = async () => {
      if (clockCheckpointLockRef.current) return;
      clockCheckpointLockRef.current = true;

      try {
        const remainingMs = Math.max(clock.endsAt.toMillis() - Date.now(), 0);
        await updateDoc(doc(db, "tournaments", id), {
          "clock.checkpointIndex": Number(clock.index || 0),
          "clock.checkpointRemainingMs": remainingMs,
          "clock.checkpointAt": Timestamp.now(),
          updatedAt: serverTimestamp(),
        });
      } catch (err) {
        console.error("Clock checkpoint failed:", err);
      } finally {
        clockCheckpointLockRef.current = false;
      }
    };

    checkpoint();
    const timer = window.setInterval(checkpoint, 15000);
    return () => window.clearInterval(timer);
  }, [id, isAdmin, isCompleted, isCancelled, clock.status, clock.index, clock.endsAt]);

  /* =========================
     Live loading: admin + tournament + entrants
  ========================= */
  useEffect(() => {
    let unsubEntrants = null;
    let unsubTournament = null;
    let unsubTimeline = null;

    combineTriggerLockRef.current = false;

    const run = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/");
        return;
      }

      try {
        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const admin = userSnap.exists() && userSnap.data().role === "admin";
        setIsAdmin(admin);
        setChecking(false);

        const tRef = doc(db, "tournaments", id);

        unsubTournament = onSnapshot(
          tRef,
          (snap) => {
            if (!snap.exists()) {
              setTournament(null);
              setLoading(false);
              setLoadingEntrants(false);
              return;
            }

            const tData = { id: snap.id, ...snap.data() };
            if (typeof tData.rebuysEnabled !== "boolean") tData.rebuysEnabled = false;
            if (typeof tData.addOnsEnabled !== "boolean") tData.addOnsEnabled = false;
            if (typeof tData.earlyBirdEnabled !== "boolean") tData.earlyBirdEnabled = false;
            if (typeof tData.dealerAppreciationEnabled !== "boolean") {
              tData.dealerAppreciationEnabled = false;
            }
            if (typeof tData.communicationsLive !== "boolean") {
              tData.communicationsLive = false;
            }
            if (typeof tData.allowSmsRegistration !== "boolean") {
              tData.allowSmsRegistration = true;
            }
            if (typeof tData.sendAnnouncementEmailNow !== "boolean") {
              tData.sendAnnouncementEmailNow = false;
            }
            if (typeof tData.sendEarlyBirdReminderEmail !== "boolean") {
              tData.sendEarlyBirdReminderEmail = false;
            }
            if (typeof tData.sendTournamentReminderEmail !== "boolean") {
              tData.sendTournamentReminderEmail = false;
            }

            if (!tData.earlyBirdDeadline && tData.earlyBirdCutoffAt) {
              tData.earlyBirdDeadline = tData.earlyBirdCutoffAt;
            }

            setTournament(tData);

            const liveBlinds =
              Array.isArray(tData.liveBlinds) && tData.liveBlinds.length
                ? tData.liveBlinds
                : Array.isArray(tData.blinds)
                ? tData.blinds
                : [];

            setBlinds(liveBlinds);
            setChips(Array.isArray(tData.chips) ? tData.chips : []);
            setPayouts(Array.isArray(tData.payouts) ? tData.payouts : []);

            if (Array.isArray(tData.payouts) && tData.payouts.length) {
              setPayoutPlaces(tData.payouts.length);
            }

            setLoading(false);

          },
          (err) => {
            console.error("Tournament snapshot error:", err);
            setLoading(false);
          }
        );

        setLoadingEntrants(true);
        const entrantsRef = collection(db, "tournaments", id, "entrants");
        const entrantsQ = query(entrantsRef, orderBy("createdAt", "asc"));

        unsubEntrants = onSnapshot(
          entrantsQ,
          (snap) => {
            const eData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            setEntrants(eData);
            setLoadingEntrants(false);
          },
          (err) => {
            console.error("Entrants snapshot error:", err);
            setLoadingEntrants(false);
          }
        );

        setLoadingTimeline(true);
        const eventsRef = collection(db, "tournaments", id, "events");
        const eventsQ = query(eventsRef, orderBy("createdAt", "desc"), limit(100));

        unsubTimeline = onSnapshot(
          eventsQ,
          (snap) => {
            setTimelineEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setLoadingTimeline(false);
          },
          (err) => {
            console.error("Tournament timeline snapshot error:", err);
            setLoadingTimeline(false);
          }
        );
      } catch (err) {
        console.error("Load error:", err);
        setChecking(false);
        setLoading(false);
        setLoadingEntrants(false);
      }
    };

    run();

    return () => {
      if (unsubEntrants) unsubEntrants();
      if (unsubTournament) unsubTournament();
      if (unsubTimeline) unsubTimeline();
    };
  }, [id, navigate]);

  useEffect(() => {
    if (checking || loading) return;
    if (!tournament) return;
    if (isAdmin) return;

    if (tournament?.communicationsLive !== true) {
      navigate("/dashboard");
    }
  }, [checking, loading, tournament, isAdmin, navigate]);

  /* =========================
     Auto-trigger combine when table count drops

     The callable owns the pause + balanced reseat transaction so the details
     page cannot create a second, conflicting live tournament state machine.
  ========================= */
  useEffect(() => {
    if (!isAdmin) return;
    if (!isRunning || isCompleted || isCancelled) return;

    if (tableCombineActive) {
      combineTriggerLockRef.current = false;
      return;
    }

    if (!combineNeeded) {
      combineTriggerLockRef.current = false;
      return;
    }

    if (activeTableCount <= 1) return;
    if (addOnPauseActive) return;
    if (combineTriggerLockRef.current) return;

    combineTriggerLockRef.current = true;

    runTournamentControl("combine_tables")
      .catch((err) => {
        console.error("Trigger table combine failed:", err);
        combineTriggerLockRef.current = false;
      });
  }, [
    isAdmin,
    isRunning,
    isCompleted,
    isCancelled,
    tableCombineActive,
    combineNeeded,
    activeTableCount,
    addOnPauseActive,
    runTournamentControl,
  ]);

  /* =========================
     Basic info save
  ========================= */
  const saveBasicInfo = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (isCompleted) return alert("Completed tournaments cannot be edited.");

    const nm = String(nameDraft || "").trim();
    const loc = String(locationDraft || "").trim();
    const dateTs = toTimestampFromLocalDatetime(dateTimeDraft);
    const buy = Number(buyInDraft || 0);
    const stack = Number(startingStackDraft || 0);

    if (!nm) return alert("Tournament name is required.");
    if (!loc) return alert("Location is required.");
    if (!dateTs) return alert("Please choose a valid date/time.");
    if (buy < 0) return alert("Buy-in must be 0 or greater.");
    if (!stack || stack <= 0) return alert("Starting stack must be greater than 0.");

    setSavingBasicInfo(true);
    try {
      const tRef = doc(db, "tournaments", id);
      await updateDoc(tRef, {
        name: nm,
        location: loc,
        date: dateTs,
        buyIn: buy,
        startingStack: stack,
        updatedAt: serverTimestamp(),
      });

      setBasicInfoDirty(false);
    } catch (e) {
      console.error("Save tournament details failed:", e);
      alert("Could not save tournament details.");
    }
    setSavingBasicInfo(false);
  };

  const saveLiveSettings = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (isCompleted) return alert("Completed tournaments cannot be edited.");

    setSavingLiveSettings(true);
    try {
      const tRef = doc(db, "tournaments", id);

      await updateDoc(tRef, {
        communicationsLive: !!communicationsLiveDraft,
        updatedAt: serverTimestamp(),
      });

      setLiveSettingsDirty(false);

      if (
        !communicationsLive &&
        !!communicationsLiveDraft &&
        (tournament?.sendAnnouncementEmailNow || tournament?.sendAnnouncementTextNow)
      ) {
        alert(
          "Tournament is now live for members. Any queued announcement email/text toggles that are ON and not yet sent can now fire."
        );
      }
    } catch (e) {
      console.error("Save live settings failed:", e);
      alert("Could not save live settings.");
    }
    setSavingLiveSettings(false);
  };

  /* =========================
     Email settings save
  ========================= */
  const saveEmailSettings = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (isCompleted) return alert("Completed tournaments cannot be edited.");

    if (sendEarlyBirdReminderEmailDraft && !earlyBirdEnabled) {
      return alert("Enable Early Bird before turning on an Early Bird reminder email.");
    }

    setSavingEmailSettings(true);
    try {
      const tRef = doc(db, "tournaments", id);
      await updateDoc(tRef, {
        sendAnnouncementEmailNow: !!sendAnnouncementEmailNowDraft,
        sendEarlyBirdReminderEmail: !!sendEarlyBirdReminderEmailDraft,
        sendTournamentReminderEmail: !!sendTournamentReminderEmailDraft,
        updatedAt: serverTimestamp(),
      });

      setEmailDirty(false);
    } catch (e) {
      console.error("Save email settings failed:", e);
      alert("Could not save email settings.");
    }
    setSavingEmailSettings(false);
  };

  /* =========================
     SMS settings save
  ========================= */
  const saveSmsSettings = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (isCompleted) return alert("Completed tournaments cannot be edited.");

    if (sendEarlyBirdReminderTextDraft && !earlyBirdEnabled) {
      return alert("Enable Early Bird before turning on an Early Bird reminder text.");
    }

    setSavingSmsSettings(true);
    try {
      const tRef = doc(db, "tournaments", id);
      const updates = {
        sendAnnouncementTextNow: !!sendAnnouncementTextNowDraft,
        sendEarlyBirdReminderText: !!sendEarlyBirdReminderTextDraft,
        sendTournamentReminderText: !!sendTournamentReminderTextDraft,
        updatedAt: serverTimestamp(),
      };

      // Once play starts, adminTournamentControl owns this field because it is
      // tied to the late-registration window and cutoff.
      if (isRegistering) {
        updates.allowSmsRegistration = !!allowSmsRegistrationDraft;
      }

      await updateDoc(tRef, updates);

      setSmsDirty(false);
    } catch (e) {
      console.error("Save SMS settings failed:", e);
      alert("Could not save SMS settings.");
    }
    setSavingSmsSettings(false);
  };

  const sendManualReminderTextNow = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (isCompleted) return alert("Completed tournaments cannot be edited.");
    if (!communicationsLive) {
      return alert("This tournament is not live for members yet.");
    }
    if (!isRegistering) {
      return alert("Manual reminder texts can only be sent while the tournament is registering.");
    }

    const lastSentLine = smsManualReminderSentAt
      ? `\n\nLast manual reminder: ${formatAdminDateTime(smsManualReminderSentAt)}${
          smsManualReminderLastSentByName ? ` by ${smsManualReminderLastSentByName}` : ""
        }`
      : "";

    const recentWarning = smsManualReminderSentRecently
      ? "\n\nWarning: a manual reminder was already sent within the last 24 hours."
      : "";

    const ok = window.confirm(
      `Send reminder text now for "${tournament?.name || "this tournament"}"?${lastSentLine}${recentWarning}`
    );
    if (!ok) return;

    setSendingManualReminderText(true);
    try {
      const sendReminder = httpsCallable(functions, "sendTournamentReminderTextNow");
      const result = await sendReminder({ tournamentId: tournament.id });
      const data = result?.data || {};

      alert(
        `Reminder text sent.\n\nSent: ${Number(data?.sentCount || 0)}\nSkipped: ${Number(
          data?.skippedCount || 0
        )}`
      );
    } catch (e) {
      console.error("Send manual reminder text failed:", e);
      alert(e?.message || "Could not send reminder text.");
    }
    setSendingManualReminderText(false);
  };

  /* =========================
     Cancel / Delete Tournament
  ========================= */
  const cancelTournament = async () => {
    if (!isAdmin) return;
    if (isCompleted) return;
    if (isCancelled) return;

    const ok = window.confirm(
      "Cancel this tournament?\n\nThis will set status to CANCELLED and stop the clock."
    );
    if (!ok) return;

    try {
      const tRef = doc(db, "tournaments", id);
      await updateDoc(tRef, {
        status: "cancelled",
        allowSmsRegistration: false,
        clock: { ...clock, status: "stopped" },
        addOnPause: deleteField(),
        tableCombine: deleteField(),
        updatedAt: serverTimestamp(),
      });

      alert("Tournament cancelled.");
    } catch (err) {
      console.error("Cancel tournament failed:", err);
      alert("Could not cancel tournament.");
    }
  };

  const deleteTournament = async () => {
    if (!isAdmin) return;
    if (!isCancelled) {
      return alert("Only cancelled tournaments can be deleted.");
    }

    const ok = window.confirm(
      "Delete this tournament permanently?\n\n" +
        "Entrants, entrant bounty-life records, tournament events, and " +
        "elimination records will also be deleted. This cannot be undone."
    );
    if (!ok) return;

    const deleteRefsInChunks = async (refs) => {
      const CHUNK_SIZE = 450;

      for (let i = 0; i < refs.length; i += CHUNK_SIZE) {
        const batch = writeBatch(db);
        refs.slice(i, i + CHUNK_SIZE).forEach((ref) => batch.delete(ref));
        await batch.commit();
      }
    };

    try {
      const tRef = doc(db, "tournaments", id);

      const [entrantsSnap, eventsSnap, eliminationsSnap] = await Promise.all([
        getDocs(collection(db, "tournaments", id, "entrants")),
        getDocs(collection(db, "tournaments", id, "events")),
        getDocs(collection(db, "tournaments", id, "eliminations")),
      ]);

      // Firestore does not cascade-delete subcollections. Clean the known
      // nested bounty-life collections before deleting entrant documents.
      for (const entrantDoc of entrantsSnap.docs) {
        const livesSnap = await getDocs(
          collection(
            db,
            "tournaments",
            id,
            "entrants",
            entrantDoc.id,
            "lives"
          )
        );
        await deleteRefsInChunks(livesSnap.docs.map((d) => d.ref));
      }

      await deleteRefsInChunks([
        ...eventsSnap.docs.map((d) => d.ref),
        ...eliminationsSnap.docs.map((d) => d.ref),
        ...entrantsSnap.docs.map((d) => d.ref),
      ]);

      await deleteDoc(tRef);

      alert("Tournament deleted.");
      navigate("/dashboard");
    } catch (err) {
      console.error("Delete tournament failed:", err);
      alert("Could not fully delete the tournament.");
    }
  };

  /* =========================
     Clock controls

     LIVE TOURNAMENT AUTHORITY:
     All live clock mutations go through adminTournamentControl so late
     registration, bounty lives, add-on pauses, table combines, and event
     logging stay in one transactional backend path.
  ========================= */
  const startClock = async () => {
    if (!isAdmin || isCompleted) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (!blinds.length) return alert("Add at least one level before starting.");

    const activeCount = activeEntrants.length;
    if (activeCount < 2) {
      return alert("Register at least two active players before starting.");
    }

    setClockControlSaving(true);
    try {
      await runTournamentControl("start");
      advanceLockRef.current = false;
      combineTriggerLockRef.current = false;
    } catch (err) {
      console.error("Start tournament failed:", err);
      alert(err?.message || "Could not start the tournament.");
    } finally {
      setClockControlSaving(false);
    }
  };

  const pauseClock = async () => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (clock.status !== "running") return;

    setClockControlSaving(true);
    try {
      await runTournamentControl("pause");
    } catch (err) {
      console.error("Pause clock failed:", err);
      alert(err?.message || "Could not pause the tournament clock.");
    } finally {
      setClockControlSaving(false);
    }
  };

  const resumeClock = async () => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (clock.status !== "paused") return;

    if (addOnPauseActive) {
      return alert("Use ‘Resume After Add-Ons’ to continue.");
    }
    if (tableCombineActive) {
      return alert(
        "Resolve the table combine first before resuming the clock."
      );
    }

    setClockControlSaving(true);
    try {
      await runTournamentControl("resume");
    } catch (err) {
      console.error("Resume clock failed:", err);
      alert(err?.message || "Could not resume the tournament clock.");
    } finally {
      setClockControlSaving(false);
    }
  };

  const applyManualClock = async ({ index, remainingMs, resume }) => {
    if (!isAdmin || isCompleted || isCancelled) return;

    if (!Array.isArray(blinds) || !blinds.length) {
      return alert("Add at least one level before adjusting the clock.");
    }

    if (!blinds[index]) {
      return alert("Select a valid blind level or break.");
    }

    if (addOnPauseActive && resume) {
      return alert("Finish the add-on pause before manually resuming.");
    }

    if (tableCombineActive && resume) {
      return alert("Resolve the table combine before manually resuming.");
    }

    setClockControlSaving(true);
    try {
      await runTournamentControl("set_clock", {
        index: Math.max(0, Number(index || 0)),
        remainingMs: Math.max(0, Number(remainingMs || 0)),
        resume: !!resume,
      });

      advanceLockRef.current = false;
      combineTriggerLockRef.current = false;
    } catch (err) {
      console.error("Manual clock adjustment failed:", err);
      alert(err?.message || "Could not update the tournament clock.");
    } finally {
      setClockControlSaving(false);
    }
  };

  const moveClockSegment = async (direction) => {
    const currentIndex = Number(clock.index || 0);
    const nextIndex = currentIndex + Number(direction || 0);

    if (nextIndex < 0 || nextIndex >= blinds.length) return;

    const nextItem = blinds[nextIndex];
    const remainingMs = Math.max(
      0,
      Number(nextItem?.minutes || 0) * 60 * 1000
    );

    await applyManualClock({
      index: nextIndex,
      remainingMs,
      resume: clock.status === "running",
    });
  };

  const recoverClockFromCheckpoint = async () => {
    const index = Number(clock?.checkpointIndex ?? clock?.index ?? 0);
    const item = blinds[index];
    const fallbackMs = Math.max(
      0,
      Number(item?.minutes || 0) * 60 * 1000
    );

    const remainingMs = Math.max(
      0,
      Number(
        clock?.checkpointRemainingMs ??
          clock?.remainingMs ??
          fallbackMs
      )
    );

    await applyManualClock({
      index,
      remainingMs,
      resume: false,
    });
  };

  const saveEarlyBirdSettings = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (!isRegistering) return alert("Early Bird settings can only be changed while registering.");

    const nextEnabled = !!earlyBirdEnabledDraft;
    const nextBonus = Math.max(0, Number(earlyBirdBonusChipsDraft || 0));
    const cutoffString = String(earlyBirdCutoffAtDraft || "").trim();

    if (nextEnabled) {
      if (nextBonus <= 0) return alert("Early Bird bonus chips must be greater than 0.");
      if (!cutoffString) return alert("Choose an Early Bird cutoff date/time.");

      const cutoffDate = new Date(cutoffString);
      if (Number.isNaN(cutoffDate.getTime())) {
        return alert("Early Bird cutoff date/time is invalid.");
      }

      const tournamentDate =
        tournament?.date?.toDate?.() || (tournament?.date ? new Date(tournament.date) : null);

      if (!tournamentDate || Number.isNaN(tournamentDate.getTime())) {
        return alert("Tournament start date is invalid.");
      }

      if (cutoffDate.getTime() >= tournamentDate.getTime()) {
        return alert("Early Bird cutoff must be before the tournament start date/time.");
      }
    }

    setSavingEarlyBirdSettings(true);

    try {
      const tRef = doc(db, "tournaments", id);

      await updateDoc(tRef, {
        earlyBirdEnabled: nextEnabled,
        earlyBirdBonusChips: nextEnabled ? nextBonus : deleteField(),
        earlyBirdDeadline: nextEnabled
          ? Timestamp.fromDate(new Date(cutoffString))
          : deleteField(),
        earlyBirdCutoffAt: deleteField(),
        updatedAt: serverTimestamp(),
      });

      if (!nextEnabled && sendEarlyBirdReminderTextDraft) {
        setSendEarlyBirdReminderTextDraft(false);
        setSmsDirty(true);
      }

      if (!nextEnabled && sendEarlyBirdReminderEmailDraft) {
        setSendEarlyBirdReminderEmailDraft(false);
        setEmailDirty(true);
      }

      setEarlyBirdDirty(false);
    } catch (e) {
      console.error("Save Early Bird settings failed:", e);
      alert("Could not save Early Bird settings.");
    }

    setSavingEarlyBirdSettings(false);
  };

  const saveDealerAppreciationSettings = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (!isRegistering) {
      return alert("Dealer appreciation settings can only be changed while registering.");
    }

    const nextEnabled = !!dealerAppreciationEnabledDraft;
    const nextCost = Math.max(0, Number(dealerAppreciationCostDraft || 0));
    const nextBonus = Math.max(0, Number(dealerAppreciationBonusChipsDraft || 0));
    const nextPrizePoolAmount = Math.max(
      0,
      Number(dealerAppreciationPrizePoolAmountDraft || 0)
    );
    const nextDealerAmount = Math.max(
      0,
      Number(dealerAppreciationDealerAmountDraft || 0)
    );

    const currentSignature = JSON.stringify({
      enabled: !!dealerAppreciationEnabled,
      cost: dealerAppreciationCost,
      bonus: dealerAppreciationBonusChips,
      prizePool: dealerAppreciationPrizePoolAmount,
      dealer: dealerAppreciationDealerAmount,
    });

    const nextSignature = JSON.stringify({
      enabled: !!nextEnabled,
      cost: nextCost,
      bonus: nextBonus,
      prizePool: nextPrizePoolAmount,
      dealer: nextDealerAmount,
    });

    if (totalDealerAppreciationOptIns > 0 && currentSignature !== nextSignature) {
      return alert(
        "Dealer appreciation settings are locked once entrants have opted in. Remove those opt-ins first if you need to change the numbers."
      );
    }

    if (nextEnabled) {
      if (nextCost <= 0) return alert("Dealer appreciation cost must be greater than 0.");
      if (nextBonus <= 0) {
        return alert("Dealer appreciation bonus chips must be greater than 0.");
      }
      if (nextPrizePoolAmount + nextDealerAmount !== nextCost) {
        return alert(
          "Dealer appreciation cost must equal prize pool portion + dealer payout portion."
        );
      }
    }

    setSavingDealerAppreciationSettings(true);

    try {
      const tRef = doc(db, "tournaments", id);

      await updateDoc(tRef, {
        dealerAppreciationEnabled: nextEnabled,
        dealerAppreciationCost: nextEnabled ? nextCost : deleteField(),
        dealerAppreciationBonusChips: nextEnabled ? nextBonus : deleteField(),
        dealerAppreciationPrizePoolAmount: nextEnabled
          ? nextPrizePoolAmount
          : deleteField(),
        dealerAppreciationDealerAmount: nextEnabled
          ? nextDealerAmount
          : deleteField(),
        updatedAt: serverTimestamp(),
      });

      setDealerAppreciationDirty(false);
    } catch (e) {
      console.error("Save dealer appreciation settings failed:", e);
      alert("Could not save dealer appreciation settings.");
    }

    setSavingDealerAppreciationSettings(false);
  };

  /* =========================
     Add-on pause + automatic advance

     The backend "advance" action decides whether the tournament should enter
     the add-on pause, close late registration, advance a level, or stop.
  ========================= */
  const resumeAfterAddOns = async () => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (!isRunning || !addOnPauseActive) return;

    setClockControlSaving(true);
    try {
      await runTournamentControl("resume_after_addons");
      advanceLockRef.current = false;
    } catch (err) {
      console.error("Resume after add-ons failed:", err);
      alert(err?.message || "Could not resume after add-ons.");
    } finally {
      setClockControlSaving(false);
    }
  };

  const advanceToNext = useCallback(async () => {
    if (!isAdmin || isCompleted || isCancelled) return;

    try {
      await runTournamentControl("advance");
      advanceLockRef.current = false;
    } catch (err) {
      console.error("Advance failed:", err);
      alert(err?.message || "Could not advance to the next segment.");
      advanceLockRef.current = false;
    }
  }, [
    isAdmin,
    isCompleted,
    isCancelled,
    runTournamentControl,
  ]);

  useEffect(() => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (clock.status !== "running") return;
    if (!clock?.endsAt?.toMillis) return;

    const remaining = clock.endsAt.toMillis() - now;

    if (remaining > 0) {
      advanceLockRef.current = false;
      return;
    }

    if (advanceLockRef.current) return;
    if (tableCombineActive) return;

    advanceLockRef.current = true;
    advanceToNext();
  }, [
    now,
    isAdmin,
    isCompleted,
    isCancelled,
    tableCombineActive,
    clock.status,
    clock.endsAt,
    advanceToNext,
  ]);

  /* =========================
     Seating / table-combine helpers
  ========================= */
  const assignOrRedrawSeats = async () => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (activeEntrants.length <= 1) {
      return alert("At least two active players are required.");
    }

    setAssigningSeatsState(true);
    try {
      await runTournamentControl("assign_seats");
    } catch (err) {
      console.error("Assign seats failed:", err);
      alert(err?.message || "Could not assign seats.");
    } finally {
      setAssigningSeatsState(false);
    }
  };

  const generateCombineSeats = async () => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (!tableCombineActive && !combineNeeded) return;
    if (activeEntrants.length <= 1) return;

    setGeneratingCombineSeats(true);
    try {
      await runTournamentControl("combine_tables");
      combineTriggerLockRef.current = false;
    } catch (err) {
      console.error("Generate combine seats failed:", err);
      alert(err?.message || "Could not generate new seating assignments.");
    } finally {
      setGeneratingCombineSeats(false);
    }
  };

  const resumeAfterCombine = async () => {
    if (!isAdmin || isCompleted || isCancelled) return;
    if (!tableCombineActive) return;

    const allActiveHaveSeats = activeEntrants.every((e) =>
      hasSeatAssignment(e)
    );

    if (!allActiveHaveSeats) {
      return alert("Generate seating assignments first before resuming.");
    }

    setClockControlSaving(true);
    try {
      await runTournamentControl("resume_after_combine");
      combineTriggerLockRef.current = false;
      advanceLockRef.current = false;
    } catch (err) {
      console.error("Resume after combine failed:", err);
      alert(err?.message || "Could not resume after table combine.");
    } finally {
      setClockControlSaving(false);
    }
  };

  /* =========================
     Save format settings
  ========================= */
  const saveFormatSettings = async () => {
    if (!isAdmin || !tournament?.id) return;
    if (isCancelled) return alert("This tournament is cancelled.");
    if (!isRegistering) return alert("Format settings can only be changed while registering.");

    const nextTournamentType =
      tournamentTypeDraft === "bounty" ? "bounty" : "standard";
    const nextBountyEnabled = nextTournamentType === "bounty";
    const nextBountyAmount = nextBountyEnabled
      ? Math.max(0, Number(bountyAmountDraft || 0))
      : 0;
    const nextPrizePoolContribution = nextBountyEnabled
      ? Math.max(0, Number(prizePoolContributionDraft || 0))
      : Math.max(0, Number(buyIn || 0));

    if (nextBountyEnabled) {
      if (nextBountyAmount <= 0) {
        return alert("Bounty amount must be greater than 0.");
      }

      if (
        Math.abs(
          nextPrizePoolContribution + nextBountyAmount - Math.max(0, Number(buyIn || 0))
        ) > 0.001
      ) {
        return alert(
          "Prize Pool / Entry + Bounty / Life must equal the tournament buy-in."
        );
      }
    }

    const nextLateRegistrationEnabled = !!lateRegistrationEnabledDraft;
    const nextLateRegistrationCutoffLevel = nextLateRegistrationEnabled
      ? toIntOrNull(lateRegistrationCutoffLevelDraft)
      : null;

    if (nextLateRegistrationEnabled) {
      if (nextLateRegistrationCutoffLevel == null) {
        return alert("Select a Late Registration Cutoff level.");
      }

      if (
        !levelIndexByLevelNumber.has(Number(nextLateRegistrationCutoffLevel))
      ) {
        return alert(
          "Late registration cutoff level is not valid for this blind structure."
        );
      }
    }

    const nextRebuysEnabled = !!rebuysEnabledDraft;
    const cutoffLevel = nextRebuysEnabled ? toIntOrNull(rebuyCutoffLevelDraft) : null;

    const nextRebuyPrice = Number(rebuyPriceDraft);
    const nextRebuyPrizePoolContribution = nextBountyEnabled
      ? Number(rebuyPrizePoolContributionDraft)
      : nextRebuyPrice;
    const nextRebuyBountyAmount = nextBountyEnabled
      ? Number(rebuyBountyAmountDraft)
      : 0;

    if (nextRebuysEnabled) {
      if (cutoffLevel == null) return alert("Select a Rebuy Cutoff level (required).");
      if (!levelIndexByLevelNumber.has(Number(cutoffLevel))) {
        return alert("Rebuy cutoff level is not valid for this blind structure.");
      }
      if (!Number.isFinite(nextRebuyPrice) || nextRebuyPrice < 0 ||
          (nextBountyEnabled && nextRebuyPrice <= 0)) {
        return alert("Enter a valid rebuy price (greater than 0 for bounty tournaments).");
      }
      if (nextBountyEnabled) {
        if (
          !Number.isFinite(nextRebuyPrizePoolContribution) || nextRebuyPrizePoolContribution < 0 ||
          !Number.isFinite(nextRebuyBountyAmount) || nextRebuyBountyAmount <= 0
        ) {
          return alert("Enter valid rebuy prize-pool and bounty amounts.");
        }
        if (
          Math.abs(
            nextRebuyPrizePoolContribution + nextRebuyBountyAmount - nextRebuyPrice
          ) > 0.001
        ) {
          return alert("Rebuy prize-pool contribution plus rebuy bounty must equal rebuy price.");
        }
      }
    }

    const nextAddOnsEnabled = !!addOnsEnabledDraft;
    const nextAddOnLevel = nextAddOnsEnabled ? toIntOrNull(addOnLevelDraft) : null;
    const nextAddOnChips = Math.max(0, Number(addOnChipsDraft || 0));
    const nextAddOnCost = Math.max(0, Number(addOnCostDraft || 0));

    if (nextAddOnsEnabled) {
      if (nextAddOnLevel == null) return alert("Choose which level add-ons occur on.");
      if (!levelIndexByLevelNumber.has(Number(nextAddOnLevel))) {
        return alert("Add-on level is not valid.");
      }
      if (nextAddOnChips <= 0) return alert("Add-on chips must be greater than 0.");
      if (nextAddOnCost < 0) return alert("Add-on cost must be 0 or greater.");
    }

    setSavingFormatSettings(true);
    try {
      const tRef = doc(db, "tournaments", id);
      await updateDoc(tRef, {
        tournamentType: nextTournamentType,
        bountyEnabled: nextBountyEnabled,
        bountyAmount: nextBountyEnabled ? Number(nextBountyAmount) : 0,
        prizePoolContribution: nextBountyEnabled
          ? Number(nextPrizePoolContribution)
          : Math.max(0, Number(buyIn || 0)),

        lateRegistrationEnabled: nextLateRegistrationEnabled,
        lateRegistrationCutoffLevel: nextLateRegistrationEnabled
          ? Number(nextLateRegistrationCutoffLevel)
          : deleteField(),
        lateRegistrationOpen: false,
        lateRegistrationClosedAt: null,
        lateRegistrationClosedByUid: null,
        lateRegistrationClosedByName: null,

        rebuysEnabled: nextRebuysEnabled,
        rebuyCutoffLevel: nextRebuysEnabled ? Number(cutoffLevel) : deleteField(),
        // Keep legacy tournaments unchanged until a rebuy configuration is saved.
        rebuyPrice: nextRebuysEnabled ? nextRebuyPrice : deleteField(),
        rebuyPrizePoolContribution: nextRebuysEnabled && nextBountyEnabled
          ? nextRebuyPrizePoolContribution
          : deleteField(),
        rebuyBountyAmount: nextRebuysEnabled && nextBountyEnabled
          ? nextRebuyBountyAmount
          : deleteField(),

        addOnsEnabled: nextAddOnsEnabled,
        addOnLevel: nextAddOnsEnabled ? Number(nextAddOnLevel) : deleteField(),
        addOnChips: nextAddOnsEnabled ? Number(nextAddOnChips) : deleteField(),
        addOnCost: nextAddOnsEnabled ? Number(nextAddOnCost) : deleteField(),

        updatedAt: serverTimestamp(),
      });

      setFormatDirty(false);
    } catch (e) {
      console.error("Save format settings failed:", e);
      alert("Could not save format settings.");
    }
    setSavingFormatSettings(false);
  };

  /* =========================
     Chips
  ========================= */
  const addChip = () => {
    const v = Number(chipValue);
    if (!v || v <= 0) return alert("Enter a valid chip value.");
    if (chips.some((c) => Number(c.value) === v)) {
      return alert("That chip value already exists.");
    }

    const newChip = { value: v, label: `$${v.toLocaleString()}`, color: chipColor };
    const next = [...chips, newChip].sort((a, b) => a.value - b.value);
    setChips(next);
    setChipValue("");
  };

  const removeChip = (value) => {
    setChips((prev) => prev.filter((c) => Number(c.value) !== Number(value)));
  };

  const saveChips = async () => {
    if (!isAdmin) return;
    if (isCancelled) return alert("This tournament is cancelled.");

    setSavingChips(true);
    try {
      const tRef = doc(db, "tournaments", id);
      await updateDoc(tRef, { chips, updatedAt: serverTimestamp() });
      alert("Chip set saved.");
    } catch (err) {
      console.error("Save chips failed:", err);
      alert("Could not save chip set.");
    }
    setSavingChips(false);
  };

  /* =========================
     Payouts
  ========================= */
  const normalizePayouts = (list) => {
    return (Array.isArray(list) ? list : [])
      .map((p) => ({ place: Number(p.place), percent: Number(p.percent) }))
      .filter((p) => p.place >= 1 && p.percent >= 0)
      .sort((a, b) => a.place - b.place);
  };

  const savePayouts = async () => {
    if (!isAdmin) return;
    if (isCancelled) return alert("This tournament is cancelled.");

    const normalized = normalizePayouts(payouts);
    if (normalized.length === 0) return alert("Add at least 1 payout place.");

    const percentSum = normalized.reduce((s, p) => s + p.percent, 0);
    if (Math.round(percentSum) !== 100) {
      return alert(`Payout percents must total 100%. Currently: ${percentSum}%`);
    }

    setSavingPayouts(true);
    try {
      const tRef = doc(db, "tournaments", id);
      await updateDoc(tRef, { payouts: normalized, updatedAt: serverTimestamp() });
      alert("Payouts saved.");
    } catch (err) {
      console.error("Save payouts failed:", err);
      alert("Could not save payouts.");
    }
    setSavingPayouts(false);
  };

  /* =========================
     Elimination modal helpers
  ========================= */
  const openEliminateModal = (targetEntrant) => {
    if (isCancelled) return alert("This tournament is cancelled.");
    if (!isRunning) {
      return alert("Eliminations are only allowed once the tournament is running.");
    }
    setElimTarget(targetEntrant);
    setElimById("");
    setElimModalOpen(true);
  };

  const closeEliminateModal = () => {
    if (elimSubmitting) return;
    setElimModalOpen(false);
    setElimTarget(null);
    setElimById("");
  };

  const confirmElimination = async () => {
    if (!elimTarget) return;
    if (!elimById) return alert("Select who eliminated this player.");
    if (isCancelled) return alert("This tournament is cancelled.");

    setElimSubmitting(true);
    try {
      await runTournamentControl("eliminate", {
        entrantId: elimTarget.id || elimTarget.uid,
        eliminatorId: elimById,
      });

      setElimModalOpen(false);
      setElimTarget(null);
      setElimById("");
    } catch (err) {
      console.error("Elimination failed:", err);
      alert(err?.message || "Could not record the elimination.");
    } finally {
      setElimSubmitting(false);
    }
  };

  /* =========================
     Winner confirmation flow
  ========================= */
  const openWinnerConfirm = (winner) => {
    if (!winner) return;
    if (!showWinnerControls) {
      return alert("Winner can only be finalized when 2 players remain.");
    }
    setWinnerConfirmTarget(winner);
    setWinnerConfirmOpen(true);
  };

  const closeWinnerConfirm = () => {
    if (winnerConfirmSubmitting) return;
    setWinnerConfirmOpen(false);
    setWinnerConfirmTarget(null);
  };

  const winnerRunnerUp = useMemo(() => {
    if (!winnerConfirmTarget) return { winner: null, runnerUp: null };
    const active = activeEntrants.filter((e) => !e.eliminated);
    const runnerUp = active.find((p) => p.id !== winnerConfirmTarget.id) || null;
    return { winner: winnerConfirmTarget, runnerUp };
  }, [winnerConfirmTarget, activeEntrants]);

  const confirmWinner = async () => {
    if (!winnerRunnerUp.winner) return;
    if (!winnerRunnerUp.runnerUp) {
      return alert("Could not find runner-up.");
    }

    setWinnerConfirmSubmitting(true);
    try {
      await runTournamentControl("declare_winner", {
        winnerId:
          winnerRunnerUp.winner.id ||
          winnerRunnerUp.winner.uid,
      });

      setWinnerConfirmOpen(false);
      setWinnerConfirmTarget(null);
    } catch (err) {
      console.error("Declare winner failed:", err);
      alert(err?.message || "Could not finalize the winner.");
    } finally {
      setWinnerConfirmSubmitting(false);
    }
  };

  /* =========================
     Render guards
  ========================= */
  if (checking) {
    return (
      <Layout>
        <p>Checking access...</p>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <p>Loading tournament...</p>
      </Layout>
    );
  }

  if (!tournament) {
    return (
      <Layout>
        <h2 className="text-3xl font-bold text-[#B87333] mb-4">Tournament Not Found</h2>
        <button
          onClick={() => navigate("/dashboard")}
          className="px-4 py-2 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
        >
          Back to Dashboard
        </button>
      </Layout>
    );
  }

  const showClockCard = isAdmin || isRunning || isCompleted;
  const showClockDetails = isRunning || isCompleted;

  return (
    <Layout>
      {isCancelled ? (
        <div className="mb-4 p-3 rounded-lg border border-red-700/40 bg-red-900/20 text-red-200">
          <div className="font-semibold">⚠️ Tournament Cancelled</div>
          <div className="text-sm text-red-200/80">
            This tournament is locked. No clock actions, rebuys, add-ons, or eliminations should be used.
          </div>
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-bold text-[#B87333]">{tournament.name}</h2>
          <p className="text-zinc-200 mt-2">{dateLabel}</p>

          {tournament?.location ? (
            <p className="text-zinc-400 mt-1">
              Location: <span className="font-semibold text-zinc-200">{tournament.location}</span>
            </p>
          ) : null}

          <p className="text-zinc-400 mt-1">
            Buy-In: <span className="font-semibold text-zinc-200">{formatMoney(buyIn)}</span>
          </p>

          <p className="text-zinc-400 mt-1">
            Starting Stack:{" "}
            <span className="font-semibold text-zinc-200">
              {(tournament.startingStack ?? 1500).toLocaleString()}
            </span>
          </p>

          <div className="flex flex-wrap gap-2 mt-3">
            <StatusPill
              tone={
                isCompleted ? "green" : isCancelled ? "red" : isRunning ? "blue" : "amber"
              }
            >
              Status: {tournament.status || "—"}
            </StatusPill>
            <StatusPill tone="zinc">Players Remaining: {computedRemaining}</StatusPill>
            <StatusPill tone="zinc">
              Tables: {activeTableCount} Active / {requiredTables} Needed
            </StatusPill>
            <StatusPill tone="zinc">Max Per Table: {maxPlayersPerTable}</StatusPill>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          {isAdmin ? (
            <button
              type="button"
              onClick={() => navigate(`/admin/tournaments/${id}/manage`)}
              className="px-4 py-2 rounded bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold"
              title="Open the admin-only live tournament control center"
            >
              Tournament Manager
            </button>
          ) : null}

          {showDisplayButton ? (
            <button
              onClick={() =>
                window.open(`/tournament/${id}/display`, "_blank", "noopener,noreferrer")
              }
              className="px-4 py-2 rounded bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-100 font-semibold"
            >
              Display Mode
            </button>
          ) : null}

          {isAdmin && isRunning && addOnPauseActive ? (
            <button
              onClick={resumeAfterAddOns}
              className="px-4 py-2 rounded bg-blue-700 hover:bg-blue-800 text-white font-semibold"
              title="Advance to the next level and resume play"
            >
              Resume After Add-Ons
            </button>
          ) : null}

          {isAdmin && !isCompleted && !isCancelled ? (
            <button
              onClick={cancelTournament}
              className="px-4 py-2 rounded bg-red-700 hover:bg-red-800 text-white font-semibold"
              title="Set status to cancelled (recommended)"
            >
              Cancel Tournament
            </button>
          ) : null}

          {isAdmin && isCancelled ? (
            <button
              onClick={deleteTournament}
              className="px-4 py-2 rounded bg-red-900 hover:bg-red-950 text-white font-semibold"
              title="Permanently delete this tournament (cannot be undone)"
            >
              Delete Tournament
            </button>
          ) : null}

          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 rounded bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold"
          >
            Back
          </button>
        </div>
      </div>

      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#B87333]">Tournament Details</h3>
            <p className="text-zinc-400 text-sm mt-1">
              Members can view these details. Admins can update them if something changes.
            </p>
          </div>

          {isAdmin ? (
            <button
              type="button"
              onClick={saveBasicInfo}
              disabled={savingBasicInfo || !basicInfoDirty || isCancelled || isCompleted}
              className={`px-4 py-2 rounded font-semibold ${
                savingBasicInfo || !basicInfoDirty || isCancelled || isCompleted
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800 text-white"
              }`}
            >
              {savingBasicInfo ? "Saving..." : "Save Tournament Details"}
            </button>
          ) : null}
        </div>

        {isAdmin ? (
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-zinc-300 text-sm mb-1">Tournament Name</label>
              <input
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={nameDraft}
                onChange={(e) => {
                  setNameDraft(e.target.value);
                  setBasicInfoDirty(true);
                }}
                disabled={isCancelled || isCompleted}
              />
            </div>

            <div>
              <label className="block text-zinc-300 text-sm mb-1">Location</label>
              <input
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={locationDraft}
                onChange={(e) => {
                  setLocationDraft(e.target.value);
                  setBasicInfoDirty(true);
                }}
                disabled={isCancelled || isCompleted}
              />
            </div>

            <div>
              <label className="block text-zinc-300 text-sm mb-1">Date & Time</label>
              <input
                type="datetime-local"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={dateTimeDraft}
                onChange={(e) => {
                  setDateTimeDraft(e.target.value);
                  setBasicInfoDirty(true);
                }}
                disabled={isCancelled || isCompleted}
              />
            </div>

            <div>
              <label className="block text-zinc-300 text-sm mb-1">Buy-In (USD)</label>
              <input
                type="number"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={buyInDraft}
                onChange={(e) => {
                  setBuyInDraft(e.target.value);
                  setBasicInfoDirty(true);
                }}
                min="0"
                step="1"
                disabled={isCancelled || isCompleted}
              />
            </div>

            <div>
              <label className="block text-zinc-300 text-sm mb-1">Starting Stack</label>
              <input
                type="number"
                className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                value={startingStackDraft}
                onChange={(e) => {
                  setStartingStackDraft(e.target.value);
                  setBasicInfoDirty(true);
                }}
                min="1"
                step="1"
                disabled={isCancelled || isCompleted}
              />
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <InfoCard label="Tournament Name" value={tournament.name || "—"} />
            <InfoCard label="Location" value={tournament.location || "—"} />
            <InfoCard label="Date & Time" value={dateLabel || "—"} />
            <InfoCard label="Buy-In" value={formatMoney(buyIn)} />
            <InfoCard
              label="Starting Stack"
              value={(tournament.startingStack ?? 1500).toLocaleString()}
            />
          </div>
        )}
      </div>

      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#B87333]">Go Live for Members</h3>
            <p className="text-zinc-400 text-sm mt-1">
              Controls whether this tournament is visible to members and whether email/SMS automation is allowed to send.
            </p>
          </div>

          {isAdmin ? (
            <button
              type="button"
              onClick={saveLiveSettings}
              disabled={savingLiveSettings || !liveSettingsDirty || isCancelled || isCompleted}
              className={`px-4 py-2 rounded font-semibold ${
                savingLiveSettings || !liveSettingsDirty || isCancelled || isCompleted
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800 text-white"
              }`}
            >
              {savingLiveSettings ? "Saving..." : "Save Live Settings"}
            </button>
          ) : null}
        </div>

        {isAdmin ? (
          <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-[#B87333]"
                checked={communicationsLiveDraft}
                onChange={(e) => {
                  setCommunicationsLiveDraft(e.target.checked);
                  setLiveSettingsDirty(true);
                }}
                disabled={isCancelled || isCompleted}
              />
              <div>
                <p className="text-zinc-100 font-semibold">
                  Visible to members + communications live
                </p>
                <p className="text-zinc-400 text-sm mt-1">
                  When OFF, this tournament is hidden from members and announcement/reminder automation is blocked.
                </p>
              </div>
            </label>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <InfoCard
              label="Member Visibility"
              value={communicationsLive ? "Live" : "Hidden"}
            />
            <InfoCard
              label="Communications"
              value={communicationsLive ? "Enabled" : "Blocked"}
            />
          </div>
        )}
      </div>

      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#B87333]">Email Notifications</h3>
            <p className="text-zinc-400 text-sm mt-1">
              Controls for tournament announcement and reminder emails to active members.
            </p>
          </div>

          {isAdmin ? (
            <button
              type="button"
              onClick={saveEmailSettings}
              disabled={savingEmailSettings || !emailDirty || isCancelled || isCompleted}
              className={`px-4 py-2 rounded font-semibold ${
                savingEmailSettings || !emailDirty || isCancelled || isCompleted
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800 text-white"
              }`}
            >
              {savingEmailSettings ? "Saving..." : "Save Email Settings"}
            </button>
          ) : null}
        </div>

        {isAdmin ? (
          <div className="grid gap-3">
            <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-[#B87333]"
                checked={sendAnnouncementEmailNowDraft}
                onChange={(e) => {
                  setSendAnnouncementEmailNowDraft(e.target.checked);
                  setEmailDirty(true);
                }}
                disabled={isCancelled || isCompleted}
              />
              <div>
                <p className="text-zinc-100 font-semibold">
                  Send tournament announcement email now
                </p>
                <p className="text-zinc-400 text-sm mt-1">
                  Sends the initial tournament announcement email to all active members.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-[#B87333]"
                checked={sendEarlyBirdReminderEmailDraft}
                onChange={(e) => {
                  setSendEarlyBirdReminderEmailDraft(e.target.checked);
                  setEmailDirty(true);
                }}
                disabled={isCancelled || isCompleted || !earlyBirdEnabled}
              />
              <div>
                <p className="text-zinc-100 font-semibold">Send Early Bird reminder email</p>
                <p className="text-zinc-400 text-sm mt-1">
                  Sends an automatic reminder before the Early Bird window closes.
                </p>
              </div>
            </label>

            <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-[#B87333]"
                checked={sendTournamentReminderEmailDraft}
                onChange={(e) => {
                  setSendTournamentReminderEmailDraft(e.target.checked);
                  setEmailDirty(true);
                }}
                disabled={isCancelled || isCompleted}
              />
              <div>
                <p className="text-zinc-100 font-semibold">Send tournament reminder email</p>
                <p className="text-zinc-400 text-sm mt-1">
                  Sends an automatic reminder before the tournament start time.
                </p>
              </div>
            </label>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <InfoCard
              label="Announcement Email"
              value={sendAnnouncementEmailNow ? "Enabled" : "Off"}
            />
            <InfoCard
              label="Early Bird Reminder"
              value={sendEarlyBirdReminderEmail ? "Enabled" : "Off"}
            />
            <InfoCard
              label="Tournament Reminder"
              value={sendTournamentReminderEmail ? "Enabled" : "Off"}
            />
          </div>
        )}
      </div>

      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#B87333]">SMS Notifications</h3>
            <p className="text-zinc-400 text-sm mt-1">
              Controls for tournament announcement and reminder texts to active members.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isAdmin ? (
              <button
                type="button"
                onClick={sendManualReminderTextNow}
                disabled={
                  sendingManualReminderText ||
                  isCancelled ||
                  isCompleted ||
                  !communicationsLive ||
                  !isRegistering
                }
                className={`px-4 py-2 rounded font-semibold ${
                  sendingManualReminderText ||
                  isCancelled ||
                  isCompleted ||
                  !communicationsLive ||
                  !isRegistering
                    ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                    : "bg-blue-700 hover:bg-blue-800 text-white"
                }`}
              >
                {sendingManualReminderText ? "Sending..." : "Send Reminder Text Now"}
              </button>
            ) : null}

            {isAdmin ? (
              <button
                type="button"
                onClick={saveSmsSettings}
                disabled={savingSmsSettings || !smsDirty || isCancelled || isCompleted}
                className={`px-4 py-2 rounded font-semibold ${
                  savingSmsSettings || !smsDirty || isCancelled || isCompleted
                    ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                    : "bg-green-700 hover:bg-green-800 text-white"
                }`}
              >
                {savingSmsSettings ? "Saving..." : "Save SMS Settings"}
              </button>
            ) : null}
          </div>
        </div>

        {isAdmin ? (
          <>
            <div className="grid gap-3">
              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendAnnouncementTextNowDraft}
                  onChange={(e) => {
                    setSendAnnouncementTextNowDraft(e.target.checked);
                    setSmsDirty(true);
                  }}
                  disabled={isCancelled || isCompleted}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">
                    Send tournament announcement text now
                  </p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends the initial tournament announcement SMS to all active opted-in members.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendEarlyBirdReminderTextDraft}
                  onChange={(e) => {
                    setSendEarlyBirdReminderTextDraft(e.target.checked);
                    setSmsDirty(true);
                  }}
                  disabled={isCancelled || isCompleted || !earlyBirdEnabled}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">Send Early Bird reminder text</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends an automatic reminder before the Early Bird window closes.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={sendTournamentReminderTextDraft}
                  onChange={(e) => {
                    setSendTournamentReminderTextDraft(e.target.checked);
                    setSmsDirty(true);
                  }}
                  disabled={isCancelled || isCompleted}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">Send tournament reminder text</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Sends an automatic reminder before the tournament start time.
                  </p>
                </div>
              </label>

              <label className="flex items-start gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-4 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={allowSmsRegistrationDraft}
                  onChange={(e) => {
                    setAllowSmsRegistrationDraft(e.target.checked);
                    setSmsDirty(true);
                  }}
                  disabled={isCancelled || isCompleted || !isRegistering}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">Allow SMS registration by reply</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Members can reply <span className="font-semibold text-zinc-200">REGISTER</span> to the club text and get added as entrants without logging into the site.
                  </p>
                  {!isRegistering ? (
                    <p className="text-zinc-500 text-xs mt-1">
                      SMS registration is only available while the tournament is registering.
                    </p>
                  ) : null}
                </div>
              </label>
            </div>

            {smsManualReminderSentAt ? (
              <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-4 text-sm">
                <div className="text-zinc-300 font-semibold">Last Manual Reminder</div>
                <div className="text-zinc-400 mt-1">
                  {formatAdminDateTime(smsManualReminderSentAt)}
                  {smsManualReminderLastSentByName
                    ? ` by ${smsManualReminderLastSentByName}`
                    : ""}
                </div>
                <div className="text-zinc-500 mt-1">
                  Sent: {smsManualReminderSentCount} • Skipped: {smsManualReminderSkippedCount}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <InfoCard
              label="Announcement Text"
              value={sendAnnouncementTextNow ? "Enabled" : "Off"}
            />
            <InfoCard
              label="Early Bird Reminder"
              value={sendEarlyBirdReminderText ? "Enabled" : "Off"}
            />
            <InfoCard
              label="Tournament Reminder"
              value={sendTournamentReminderText ? "Enabled" : "Off"}
            />
            <InfoCard
              label="SMS Registration"
              value={allowSmsRegistrationDraft ? "Reply REGISTER enabled" : "Off"}
            />
          </div>
        )}
      </div>

      {showClockCard ? (
        <TournamentClockCard
          clock={clock}
          activeItem={activeItem}
          timeRemainingMs={timeRemainingMs}
          msToMMSS={msToMMSS}
          isAdmin={isAdmin}
          isCompleted={isCompleted}
          showDetails={showClockDetails}
          tournamentStatus={tournament.status || "registering"}
          blinds={blinds}
          saving={clockControlSaving}
          onStartClock={startClock}
          onPauseClock={pauseClock}
          onResumeClock={resumeClock}
          onApplyManualClock={applyManualClock}
          onPreviousSegment={() => moveClockSegment(-1)}
          onNextSegment={() => moveClockSegment(1)}
          onRecoverClock={recoverClockFromCheckpoint}
        />
      ) : null}

      <TournamentTimelineCard events={timelineEvents} loading={loadingTimeline} />

      <TableCombineBanner
        combine={tableCombine}
        maxPlayersPerTable={maxPlayersPerTable}
        activeTableCount={activeTableCount}
        requiredTables={requiredTables}
        playersRemaining={computedRemaining}
        onGenerate={generateCombineSeats}
        onResume={resumeAfterCombine}
        generating={generatingCombineSeats || assigningSeatsState}
        canResume={activeEntrants.every((e) => hasSeatAssignment(e))}
      />

      {seatedTables.length > 0 ? (
        <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
          <div className="flex items-center justify-between gap-4 mb-4">
            <div>
              <h3 className="text-xl font-semibold text-[#B87333]">Current Seating Order</h3>
              <p className="text-zinc-400 text-sm mt-1">
                Active players are grouped by table and seat so you can seat and combine quickly.
              </p>
            </div>

            {isAdmin && !isCompleted ? (
              <button
                type="button"
                onClick={assignOrRedrawSeats}
                disabled={assigningSeatsState}
                className={`px-4 py-2 rounded font-semibold ${
                  assigningSeatsState
                    ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                    : "bg-[#B87333] hover:bg-[#A46230] text-zinc-950"
                }`}
              >
                {assigningSeatsState ? "Assigning..." : "Redraw Seating"}
              </button>
            ) : null}
          </div>

          <div className="grid xl:grid-cols-2 gap-4">
            {seatedTables.map((table) => (
              <div
                key={`table-${table.tableNumber}`}
                className="rounded-lg border border-zinc-800 bg-zinc-950 p-4"
              >
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-zinc-100 font-semibold">
                    Table {table.tableNumber}
                  </div>
                  <StatusPill tone="zinc">{table.players.length} Players</StatusPill>
                </div>

                <div className="space-y-2">
                  {table.players.map((player) => {
                    const seatNo = getSeatNumber(player);
                    const role = getSeatRole(player);

                    return (
                      <div
                        key={player.id || player.uid}
                        className="rounded-md border border-zinc-800 bg-zinc-900/60 px-3 py-2 flex items-center justify-between gap-3"
                      >
                        <div>
                          <div className="text-zinc-100 font-medium">{entrantLabel(player)}</div>
                          <div className="text-zinc-400 text-sm mt-1">
                            Seat {seatNo}
                            {role ? ` — ${role}` : ""}
                          </div>
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                          {player.earlyBirdQualified ? (
                            <StatusPill tone="green">Early Bird</StatusPill>
                          ) : null}
                          {player.dealerAppreciationOptIn ? (
                            <StatusPill tone="blue">Dealer Appr</StatusPill>
                          ) : null}
                          {player.addOnTaken ? (
                            <StatusPill tone="amber">Add-On</StatusPill>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <EarlyBirdCard
        isAdmin={isAdmin}
        isCompleted={isCompleted}
        isRegistering={isRegistering}
        isRunning={isRunning}
        formatMoney={formatMoney}
        tournamentDate={tournament?.date}
        now={now}
        earlyBirdEnabled={earlyBirdEnabled}
        earlyBirdBonusChips={earlyBirdBonusChips}
        earlyBirdCutoffAt={earlyBirdDeadline}
        earlyBirdEnabledDraft={earlyBirdEnabledDraft}
        setEarlyBirdEnabledDraft={(v) => {
          setEarlyBirdEnabledDraft(v);
          setEarlyBirdDirty(true);
        }}
        earlyBirdBonusChipsDraft={earlyBirdBonusChipsDraft}
        setEarlyBirdBonusChipsDraft={(v) => {
          setEarlyBirdBonusChipsDraft(v);
          setEarlyBirdDirty(true);
        }}
        earlyBirdCutoffAtDraft={earlyBirdCutoffAtDraft}
        setEarlyBirdCutoffAtDraft={(v) => {
          setEarlyBirdCutoffAtDraft(v);
          setEarlyBirdDirty(true);
        }}
        earlyBirdDirty={earlyBirdDirty}
        savingEarlyBirdSettings={savingEarlyBirdSettings}
        onSaveEarlyBirdSettings={saveEarlyBirdSettings}
      />

      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-[#B87333]">Dealer Appreciation</h3>
            <p className="text-zinc-400 text-sm mt-1">
              Optional player opt-in at registration for bonus chips and dealer support.
            </p>
          </div>

          {isAdmin ? (
            <button
              type="button"
              onClick={saveDealerAppreciationSettings}
              disabled={
                savingDealerAppreciationSettings ||
                !dealerAppreciationDirty ||
                isCancelled ||
                !isRegistering
              }
              className={`px-4 py-2 rounded font-semibold ${
                savingDealerAppreciationSettings ||
                !dealerAppreciationDirty ||
                isCancelled ||
                !isRegistering
                  ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                  : "bg-green-700 hover:bg-green-800 text-white"
              }`}
            >
              {savingDealerAppreciationSettings ? "Saving..." : "Save Dealer Appreciation"}
            </button>
          ) : null}
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <InfoCard
            label="Status"
            value={dealerAppreciationEnabled ? "Enabled" : "Disabled"}
          />
          <InfoCard label="Opt-Ins" value={String(totalDealerAppreciationOptIns)} />
          <InfoCard
            label="Prize Pool Added"
            value={formatMoney(totalDealerAppreciationPrizePool)}
          />
          <InfoCard
            label="Dealer Payout"
            value={formatMoney(totalDealerAppreciationDealerPayout)}
          />
        </div>

        {isAdmin ? (
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-5 w-5 accent-[#B87333]"
                  checked={dealerAppreciationEnabledDraft}
                  onChange={(e) => {
                    setDealerAppreciationEnabledDraft(e.target.checked);
                    setDealerAppreciationDirty(true);
                  }}
                  disabled={isCancelled || !isRegistering}
                />
                <div>
                  <p className="text-zinc-100 font-semibold">Enable Dealer Appreciation</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Lets players opt in for bonus chips and dealer support.
                  </p>
                </div>
              </label>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-zinc-300 text-sm mb-1">Cost</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={dealerAppreciationCostDraft}
                    onChange={(e) => {
                      setDealerAppreciationCostDraft(e.target.value);
                      setDealerAppreciationDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 text-sm mb-1">Bonus Chips</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={dealerAppreciationBonusChipsDraft}
                    onChange={(e) => {
                      setDealerAppreciationBonusChipsDraft(e.target.value);
                      setDealerAppreciationDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 text-sm mb-1">Prize Pool Portion</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={dealerAppreciationPrizePoolAmountDraft}
                    onChange={(e) => {
                      setDealerAppreciationPrizePoolAmountDraft(e.target.value);
                      setDealerAppreciationDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 text-sm mb-1">Dealer Payout Portion</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded"
                    value={dealerAppreciationDealerAmountDraft}
                    onChange={(e) => {
                      setDealerAppreciationDealerAmountDraft(e.target.value);
                      setDealerAppreciationDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                  />
                </div>
              </div>

              <div className="mt-4 rounded-lg border border-amber-500/20 bg-amber-500/10 p-3">
                <p className="text-sm text-amber-200 font-medium">
                  Live totals
                </p>
                <p className="text-zinc-300 mt-1">
                  Bonus chips awarded:{" "}
                  <span className="font-semibold">
                    {totalDealerAppreciationBonusChipsAwarded.toLocaleString()}
                  </span>
                </p>
                {totalDealerAppreciationOptIns > 0 ? (
                  <p className="text-xs text-amber-200/80 mt-2">
                    Dealer appreciation values are effectively locked once entrants opt in.
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="bg-zinc-900 p-4 rounded-lg border border-zinc-800 mb-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between mb-5">
          <div>
            <h3 className="text-xl font-semibold text-[#B87333]">
              Tournament Type & Registration Window
            </h3>
            <p className="text-zinc-400 text-sm mt-1">
              Edit bounty accounting and the late-registration cutoff while the tournament is registering.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <StatusPill tone={bountyEnabled ? "amber" : "zinc"}>
              {bountyEnabled ? "♠ Bounty NLHE" : "Standard NLHE"}
            </StatusPill>
            {lateRegistrationEnabled ? (
              <StatusPill tone={lateRegistrationOpen ? "green" : "blue"}>
                Late Reg: {lateRegistrationOpen ? "OPEN" : `Through Level ${lateRegistrationCutoffLevel || "—"}`}
              </StatusPill>
            ) : (
              <StatusPill tone="zinc">Late Reg: Disabled</StatusPill>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-4 mb-5">
          <InfoCard
            label="Tournament Type"
            value={bountyEnabled ? "Bounty NLHE" : "Standard NLHE"}
          />
          <InfoCard
            label="Buy-In"
            value={formatMoney(buyIn)}
          />
          <InfoCard
            label="Prize Pool / Entry"
            value={formatMoney(bountyEnabled ? prizePoolContribution : buyIn)}
          />
          <InfoCard
            label="Bounty / Life"
            value={bountyEnabled ? formatMoney(bountyAmount) : "—"}
            subtext={bountyEnabled ? "Initial entry bounty" : ""}
          />
        </div>

        {isAdmin ? (
          <div className="space-y-5">
            <div className="grid md:grid-cols-2 gap-4">
              <button
                type="button"
                disabled={isCancelled || !isRegistering}
                onClick={() => {
                  setTournamentTypeDraft("standard");
                  setBountyAmountDraft("0");
                  setPrizePoolContributionDraft(String(Math.max(0, Number(buyIn || 0))));
                  setFormatDirty(true);
                }}
                className={`text-left rounded-xl border p-4 transition ${
                  tournamentTypeDraft === "standard"
                    ? "border-[#B87333] bg-[#B87333]/10"
                    : "border-zinc-800 bg-zinc-950"
                } disabled:opacity-50`}
              >
                <div className="text-zinc-100 font-semibold">Standard NLHE</div>
                <div className="text-zinc-500 text-sm mt-1">
                  Full buy-in contributes to the regular prize pool.
                </div>
              </button>

              <button
                type="button"
                disabled={isCancelled || !isRegistering}
                onClick={() => {
                  const currentBuyIn = Math.max(0, Number(buyIn || 0));
                  const suggestedBounty = Math.min(
                    currentBuyIn,
                    Math.max(10, Number(bountyAmountDraft || 0))
                  );
                  setTournamentTypeDraft("bounty");
                  setBountyAmountDraft(String(suggestedBounty));
                  setPrizePoolContributionDraft(
                    String(Math.max(0, currentBuyIn - suggestedBounty))
                  );
                  setFormatDirty(true);
                }}
                className={`text-left rounded-xl border p-4 transition ${
                  tournamentTypeDraft === "bounty"
                    ? "border-[#B87333] bg-[#B87333]/10"
                    : "border-zinc-800 bg-zinc-950"
                } disabled:opacity-50`}
              >
                <div className="text-zinc-100 font-semibold">♠ Bounty NLHE</div>
                <div className="text-zinc-500 text-sm mt-1">
                  Set separate prize-pool and bounty amounts for entry and rebuys.
                </div>
              </button>
            </div>

            {tournamentTypeDraft === "bounty" ? (
              <div className="grid md:grid-cols-3 gap-4 rounded-xl border border-[#B87333]/30 bg-[#B87333]/5 p-4">
                <div>
                  <label className="block text-zinc-300 text-sm mb-1">
                    Prize Pool / Entry
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={prizePoolContributionDraft}
                    onChange={(e) => {
                      setPrizePoolContributionDraft(e.target.value);
                      setFormatDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded disabled:opacity-50"
                  />
                </div>

                <div>
                  <label className="block text-zinc-300 text-sm mb-1">
                    Bounty / Life
                  </label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={bountyAmountDraft}
                    onChange={(e) => {
                      setBountyAmountDraft(e.target.value);
                      setFormatDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded disabled:opacity-50"
                  />
                </div>

                <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
                  <div className="text-xs uppercase tracking-wide text-zinc-500">
                    Buy-In Check
                  </div>
                  <div className="text-zinc-100 font-semibold mt-1">
                    {formatMoney(Number(prizePoolContributionDraft || 0))} +{" "}
                    {formatMoney(Number(bountyAmountDraft || 0))}
                  </div>
                  <div
                    className={`text-xs mt-1 ${
                      Math.abs(
                        Number(prizePoolContributionDraft || 0) +
                          Number(bountyAmountDraft || 0) -
                          Number(buyIn || 0)
                      ) < 0.001
                        ? "text-green-400"
                        : "text-red-400"
                    }`}
                  >
                    {Math.abs(
                      Number(prizePoolContributionDraft || 0) +
                        Number(bountyAmountDraft || 0) -
                        Number(buyIn || 0)
                    ) < 0.001
                      ? "Matches buy-in"
                      : "Must equal buy-in"}
                  </div>
                </div>
              </div>
            ) : null}

            <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-4 space-y-3">
              <div className="font-semibold text-zinc-100">Rebuy Pricing</div>
              <p className="text-xs text-zinc-400">
                These amounts apply when rebuys are enabled below. Legacy tournaments keep
                their original pricing until the new settings are saved.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-zinc-300 text-sm mb-1">Rebuy Price</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={rebuyPriceDraft}
                    onChange={(e) => {
                      setRebuyPriceDraft(e.target.value);
                      setFormatDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                    className="w-full p-3 bg-zinc-800 text-zinc-100 rounded disabled:opacity-50"
                  />
                </div>
                {tournamentTypeDraft === "bounty" ? (
                  <>
                    <div>
                      <label className="block text-zinc-300 text-sm mb-1">Rebuy Prize Pool</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={rebuyPrizePoolContributionDraft}
                        onChange={(e) => {
                          setRebuyPrizePoolContributionDraft(e.target.value);
                          setFormatDirty(true);
                        }}
                        disabled={isCancelled || !isRegistering}
                        className="w-full p-3 bg-zinc-800 text-zinc-100 rounded disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-300 text-sm mb-1">Rebuy Bounty / Life</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={rebuyBountyAmountDraft}
                        onChange={(e) => {
                          setRebuyBountyAmountDraft(e.target.value);
                          setFormatDirty(true);
                        }}
                        disabled={isCancelled || !isRegistering}
                        className="w-full p-3 bg-zinc-800 text-zinc-100 rounded disabled:opacity-50"
                      />
                    </div>
                  </>
                ) : null}
              </div>
              {tournamentTypeDraft === "bounty" ? (
                <p className={`text-xs ${
                  Math.abs(
                    Number(rebuyPrizePoolContributionDraft || 0) +
                      Number(rebuyBountyAmountDraft || 0) -
                      Number(rebuyPriceDraft || 0)
                  ) < 0.001
                    ? "text-green-400"
                    : "text-red-400"
                }`}>
                  Rebuy: {formatMoney(Number(rebuyPrizePoolContributionDraft || 0))}
                  {" + "}{formatMoney(Number(rebuyBountyAmountDraft || 0))}
                  {" = "}{formatMoney(Number(rebuyPriceDraft || 0))}
                  {" — "}
                  {Math.abs(
                    Number(rebuyPrizePoolContributionDraft || 0) +
                      Number(rebuyBountyAmountDraft || 0) -
                      Number(rebuyPriceDraft || 0)
                  ) < 0.001 ? "Balanced" : "Amounts must match"}
                </p>
              ) : null}
            </div>

            <div className="grid md:grid-cols-2 gap-4 rounded-xl border border-zinc-800 bg-zinc-950 p-4">
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1 h-5 w-5 accent-[#B87333]"
                    checked={lateRegistrationEnabledDraft}
                    onChange={(e) => {
                      setLateRegistrationEnabledDraft(e.target.checked);
                      setFormatDirty(true);
                    }}
                    disabled={isCancelled || !isRegistering}
                  />
                  <div>
                    <p className="text-zinc-100 font-semibold">
                      Enable Late Registration
                    </p>
                    <p className="text-zinc-500 text-xs mt-1">
                      New first-time entrants may join after the tournament starts.
                    </p>
                  </div>
                </label>
              </div>

              <div>
                <label className="block text-zinc-300 text-sm mb-1">
                  Late Registration Cutoff
                </label>
                <select
                  value={lateRegistrationCutoffLevelDraft}
                  onChange={(e) => {
                    setLateRegistrationCutoffLevelDraft(e.target.value);
                    setFormatDirty(true);
                  }}
                  disabled={
                    !lateRegistrationEnabledDraft || isCancelled || !isRegistering
                  }
                  className="w-full p-3 bg-zinc-800 text-zinc-100 rounded disabled:opacity-50"
                >
                  <option value="">Select level</option>
                  {levelItems.map((it) => (
                    <option key={`late-reg-${it.level}`} value={it.level}>
                      End of Level {it.level}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-zinc-500">
                Tournament type, bounty split, and late-registration cutoff lock when the tournament starts.
              </p>

              <button
                type="button"
                onClick={saveFormatSettings}
                disabled={
                  savingFormatSettings ||
                  !formatDirty ||
                  isCancelled ||
                  !isRegistering
                }
                className={`px-4 py-3 rounded font-semibold ${
                  savingFormatSettings ||
                  !formatDirty ||
                  isCancelled ||
                  !isRegistering
                    ? "bg-zinc-700 text-zinc-300 cursor-not-allowed"
                    : "bg-green-700 hover:bg-green-800 text-white"
                }`}
              >
                {savingFormatSettings ? "Saving..." : "Save Tournament Format"}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <FormatCard
        tournament={tournament}
        isAdmin={isAdmin}
        isCompleted={isCompleted}
        isRegistering={isRegistering}
        isRunning={isRunning}
        formatMoney={formatMoney}
        buyIn={buyIn}
        rebuyPrice={rebuyPrice}
        totalRebuys={totalRebuys}
        totalAddOns={totalAddOns}
        rebuysWindowOpen={rebuysWindowOpen}
        addOnsWindowOpen={addOnsWindowOpen}
        rebuysEnabled={rebuysEnabled}
        rebuyCutoffLevel={rebuyCutoffLevel}
        addOnsEnabled={addOnsEnabled}
        addOnLevel={addOnLevel}
        addOnChips={addOnChips}
        addOnCost={addOnCost}
        levelItems={levelItems}
        rebuysEnabledDraft={rebuysEnabledDraft}
        setRebuysEnabledDraft={(v) => {
          setRebuysEnabledDraft(v);
          setFormatDirty(true);
        }}
        rebuyCutoffLevelDraft={rebuyCutoffLevelDraft}
        setRebuyCutoffLevelDraft={(v) => {
          setRebuyCutoffLevelDraft(v);
          setFormatDirty(true);
        }}
        addOnsEnabledDraft={addOnsEnabledDraft}
        setAddOnsEnabledDraft={(v) => {
          setAddOnsEnabledDraft(v);
          setFormatDirty(true);
        }}
        addOnLevelDraft={addOnLevelDraft}
        setAddOnLevelDraft={(v) => {
          setAddOnLevelDraft(v);
          setFormatDirty(true);
        }}
        addOnChipsDraft={addOnChipsDraft}
        setAddOnChipsDraft={(v) => {
          setAddOnChipsDraft(v);
          setFormatDirty(true);
        }}
        addOnCostDraft={addOnCostDraft}
        setAddOnCostDraft={(v) => {
          setAddOnCostDraft(v);
          setFormatDirty(true);
        }}
        formatDirty={formatDirty}
        savingFormatSettings={savingFormatSettings}
        onSaveFormatSettings={saveFormatSettings}
      />

      <PayoutsCard
        tournament={tournament}
        prizePool={prizePool}
        buyIn={buyIn}
        entrantsCount={entrantsCount}
        entryPrizeContribution={bountyEnabled ? prizePoolContribution : buyIn}
        rebuyPrice={rebuyPrice}
        rebuyPrizeContribution={rebuyPrizePoolContribution}
        rebuyBountyAmount={rebuyBountyAmount}
        bountyEnabled={bountyEnabled}
        rebuysEnabled={rebuysEnabled}
        totalRebuys={totalRebuys}
        addOnsEnabled={addOnsEnabled}
        totalAddOns={totalAddOns}
        addOnCost={addOnCost}
        dealerAppreciationEnabled={dealerAppreciationEnabled}
        dealerAppreciationOptIns={totalDealerAppreciationOptIns}
        dealerAppreciationCost={dealerAppreciationCost}
        dealerAppreciationPrizePoolPerOptIn={dealerAppreciationPrizePoolAmount}
        dealerAppreciationDealerPerOptIn={dealerAppreciationDealerAmount}
        totalDealerAppreciationPrizePool={totalDealerAppreciationPrizePool}
        totalDealerAppreciationDealerPayout={totalDealerAppreciationDealerPayout}
        payouts={payouts}
        setPayouts={setPayouts}
        payoutPlaces={payoutPlaces}
        setPayoutPlaces={setPayoutPlaces}
        savePayouts={savePayouts}
        savingPayouts={savingPayouts}
        isAdmin={isAdmin}
        isCompleted={isCompleted}
        payoutAmountForPlace={scheduledPayoutAmountForPlace}
      />

      <EntrantsPanel
        tournamentId={id}
        tournament={tournament}
        entrants={entrantsSorted}
        loadingEntrants={loadingEntrants}
        isAdmin={isAdmin}
        isCompleted={isCompleted}
        isRegistering={isRegistering}
        isRunning={isRunning}
        showWinnerControls={showWinnerControls}
        payoutByPlace={payoutByPlace}
        formatMoney={formatMoney}
        onAssignSeats={assignOrRedrawSeats}
        assigningSeats={assigningSeatsState}
        onRemoveEntrantWhileRegistering={(target) =>
          removeEntrantWhileRegistering({
            db,
            tournamentId: id,
            isAdmin,
            isCompleted,
            isRegistering,
            targetEntrant: target,
            entrantLabel,
            setEntrants,
            setTournament,
          })
        }
        onOpenEliminateModal={openEliminateModal}
        onDeclareWinner={openWinnerConfirm}
        entrantUidSet={entrantUidSet}
        onAddEntrantByAdmin={async (member, options) => {
          if (isRunning) {
            if (!lateRegistrationOpen) {
              return alert("Late registration is closed.");
            }

            try {
              await runTournamentControl("add_late_entrant", {
                memberUid: member?.uid || member?.id,
                dealerAppreciationOptIn:
                  options?.dealerAppreciationOptIn === true,
              });
            } catch (err) {
              console.error("Add late entrant failed:", err);
              alert(err?.message || "Could not add the late entrant.");
            }
            return;
          }

          return addEntrantByAdmin({
            db,
            tournamentId: id,
            isAdmin,
            isCompleted,
            entrantUidSet,
            member,
            options,
            setTournament,
          });
        }}
        rebuysEnabled={rebuysEnabled}
        buyIn={buyIn}
        rebuysWindowOpen={rebuysWindowOpen}
        rebuyCutoffLevel={rebuyCutoffLevel}
        onRebuyEntrant={async (target) => {
          try {
            await runTournamentControl("rebuy", {
              entrantId: target?.id || target?.uid,
            });
          } catch (err) {
            console.error("Rebuy failed:", err);
            alert(err?.message || "Could not record the rebuy.");
          }
        }}
        addOnsEnabled={addOnsEnabled}
        addOnsWindowOpen={addOnsWindowOpen}
        addOnLevel={addOnLevel}
        addOnChips={addOnChips}
        addOnCost={addOnCost}
        onAddOnEntrant={async (target) => {
          try {
            await runTournamentControl("add_on", {
              entrantId: target?.id || target?.uid,
            });
          } catch (err) {
            console.error("Add-on failed:", err);
            alert(err?.message || "Could not record the add-on.");
          }
        }}
      />

      <ChipDenominationsCard
        chips={chips}
        chipValue={chipValue}
        setChipValue={setChipValue}
        chipColor={chipColor}
        setChipColor={setChipColor}
        onAddChip={addChip}
        onRemoveChip={removeChip}
        onSaveChips={saveChips}
        savingChips={savingChips}
        isAdmin={isAdmin}
        isCompleted={isCompleted}
      />

      <BlindStructureCard
        blinds={blinds}
        setBlinds={setBlinds}
        isAdmin={isAdmin && !isRunning && !isCompleted && !isCancelled}
        savingStructure={savingStructure}
        setSavingStructure={setSavingStructure}
        tournamentId={id}
        db={db}
        serverTimestamp={serverTimestamp}
      />

      <EliminationModal
        open={elimModalOpen}
        target={elimTarget}
        activeEntrants={activeEntrants}
        elimById={elimById}
        setElimById={setElimById}
        onCancel={closeEliminateModal}
        onConfirm={confirmElimination}
        submitting={elimSubmitting}
      />

      <WinnerConfirmModal
        open={winnerConfirmOpen}
        winner={winnerRunnerUp.winner}
        runnerUp={winnerRunnerUp.runnerUp}
        payoutAmountForPlace={payoutAmountForPlace}
        onCancel={closeWinnerConfirm}
        onConfirm={confirmWinner}
        submitting={winnerConfirmSubmitting}
      />
    </Layout>
  );
}